import { existsSync, mkdirSync, readFileSync, writeFileSync, rmSync, statSync } from 'fs';
import { join, dirname, basename } from 'path';
import { getCredentialManager } from '../credentials/index.ts';
import { getOrCreateLatestSession, type SessionConfig } from '../sessions/index.ts';
import {
  discoverWorkspacesInDefaultLocation,
  loadWorkspaceConfig,
  saveWorkspaceConfig,
  createWorkspaceAtPath,
  isValidWorkspace,
} from '../workspaces/storage.ts';
import { findIconFile } from '../utils/icon.ts';
import { initializeDocs } from '../docs/index.ts';
import { ensureDefaultSources } from '../sources/default-sources.ts';
import { ensureDefaultSkills } from '../skills/default-skills.ts';
import { expandPath, toPortablePath, getBundledAssetsDir } from '../utils/paths.ts';
import { debug } from '../utils/debug.ts';
import { readJsonFileSync } from '../utils/files.ts';
import { CONFIG_DIR } from './paths.ts';
import type { StoredAttachment, StoredMessage } from '@craft-agent/core/types';
import type { Plan } from '../agent/plan-types.ts';
import type { PermissionMode } from '../agent/mode-manager.ts';
import type { ThinkingLevel } from '../agent/thinking-levels.ts';
import { isValidThinkingLevel } from '../agent/thinking-levels.ts';
import { parsePermissionMode, PERMISSION_MODE_ORDER } from '../agent/mode-types.ts';
import { type ConfigDefaults } from './config-defaults-schema.ts';
import { isValidThemeFile } from './validators.ts';

// Re-export CONFIG_DIR for convenience (centralized in paths.ts)
export { CONFIG_DIR } from './paths.ts';

// Re-export base types from core (single source of truth)
export type {
  Workspace,
  McpAuthType,
  AuthType,
  OAuthCredentials,
} from '@craft-agent/core/types';

// Import for local use
import type { Workspace, AuthType } from '@craft-agent/core/types';

// Import LLM connection types and constants
import type { LlmConnection } from './llm-connections.ts';
import { MANAGED_MODEL_PRESETS, getDefaultManagedModelPreset, isManagedLlmConnectionSlug, isValidProviderAuthCombination, getDefaultModelsForConnection, getDefaultModelForConnection, isPiProvider } from './llm-connections.ts';
import {
  getModelById,
  getModelProvider,
} from './models.ts';

// Config stored in JSON file (credentials stored in encrypted file, not here)
export interface StoredConfig {
  // LLM Connections (authoritative source for auth and model config)
  llmConnections?: LlmConnection[];
  defaultLlmConnection?: string;  // Slug of default connection for new sessions
  defaultThinkingLevel?: ThinkingLevel;  // App-level default thinking level for new sessions
  managedModelDefaultsMigrated?: boolean;  // One-time bundled Anthropic → OpenAI default migration
  managedAstraDefaultMigrated?: boolean;  // One-time Sol → Astra default migration
  managedOpus55DefaultMigrated?: boolean;  // One-time Astra → Opus 5.5 default migration

  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  activeSessionId: string | null;  // Currently active session (primary scope)
  // Notifications
  notificationsEnabled?: boolean;  // Desktop notifications for task completion (default: true)
  // Appearance
  colorTheme?: string;  // ID of selected preset theme (e.g., 'dracula', 'nord'). Default: 'default'
  // Auto-update
  dismissedUpdateVersion?: string;  // Version that user dismissed (skip notifications for this version)
  // Input settings
  autoCapitalisation?: boolean;  // Auto-capitalize first letter when typing (default: true)
  sendMessageKey?: 'enter' | 'cmd-enter';  // Key to send messages (default: 'enter')
  spellCheck?: boolean;  // Enable spell check in input (default: false)
  // Power settings
  keepAwakeWhileRunning?: boolean;  // Prevent screen sleep while sessions are running (default: false)
  // Tool metadata
  richToolDescriptions?: boolean;  // Add intent/action metadata to all tool calls (default: true)
  // Windows: path to Git Bash (bash.exe) for the SDK subprocess
  gitBashPath?: string;
  // Whether the user has completed initial onboarding (welcome screen)
  onboardingCompleted?: boolean;
  // Email verification (OTP gate)
  emailVerified?: boolean;
  verifiedEmail?: string;
  // Remote access
  remoteAccessEnabled?: boolean;  // Auto-start remote access server on launch (default: false)
  remoteAccessPort?: number;      // Port for remote access server (default: 9100)
}

const CONFIG_FILE = join(CONFIG_DIR, 'config.json');
const CONFIG_DEFAULTS_FILE = join(CONFIG_DIR, 'config-defaults.json');

// Track if config-defaults have been synced this session (prevents re-sync on hot reload)
let configDefaultsSynced = false;

// Track which workspace paths have already had defaults ensured in this process.
// Prevents ensureDefaultSources/ensureDefaultSkills from running on every loadStoredConfig() call.
const ensuredWorkspaces = new Set<string>();

/**
 * Sync config-defaults.json from bundled assets.
 * Always writes on launch to ensure defaults are up-to-date with the running version.
 * Follows the same pattern as docs, themes, and other bundled assets.
 *
 * Source of truth: apps/electron/resources/config-defaults.json
 */
function syncConfigDefaults(): void {
  if (configDefaultsSynced) return;
  configDefaultsSynced = true;

  // Get bundled config-defaults.json from resources folder
  const bundledDir = getBundledAssetsDir('.');
  if (!bundledDir) {
    debug('[config] No bundled assets dir found - config-defaults will not be synced');
    return;
  }

  const bundledFile = join(bundledDir, 'config-defaults.json');
  if (!existsSync(bundledFile)) {
    debug('[config] Bundled config-defaults.json not found at: ' + bundledFile);
    return;
  }

  // Sync from bundled file (same pattern as docs)
  const content = readFileSync(bundledFile, 'utf-8');
  writeFileSync(CONFIG_DEFAULTS_FILE, content, 'utf-8');
  debug('[config] Synced config-defaults.json from bundled assets');
}

/**
 * Load config defaults from ~/.origin-relay/config-defaults.json
 * This file is synced from bundled assets on every launch.
 */
export function loadConfigDefaults(): ConfigDefaults {
  if (!existsSync(CONFIG_DEFAULTS_FILE)) {
    throw new Error('config-defaults.json not found at ' + CONFIG_DEFAULTS_FILE + '. Ensure ensureConfigDir() was called at startup.');
  }

  const defaults = readJsonFileSync<ConfigDefaults>(CONFIG_DEFAULTS_FILE);

  const parsedPermissionMode =
    typeof defaults.workspaceDefaults?.permissionMode === 'string'
      ? parsePermissionMode(defaults.workspaceDefaults.permissionMode)
      : null;
  defaults.workspaceDefaults.permissionMode = parsedPermissionMode ?? 'allow-all';

  const rawCyclable = Array.isArray(defaults.workspaceDefaults?.cyclablePermissionModes)
    ? defaults.workspaceDefaults.cyclablePermissionModes
    : [];

  const normalizedCyclable: PermissionMode[] = [];
  for (const mode of rawCyclable) {
    if (typeof mode !== 'string') continue;
    const parsed = parsePermissionMode(mode);
    if (!parsed) continue;
    if (!normalizedCyclable.includes(parsed)) {
      normalizedCyclable.push(parsed);
    }
  }

  defaults.workspaceDefaults.cyclablePermissionModes =
    normalizedCyclable.length >= 2 ? normalizedCyclable : [...PERMISSION_MODE_ORDER];

  return defaults;
}

/**
 * Ensure config-defaults.json exists and is up-to-date.
 * Syncs from bundled assets on every launch (like docs, themes, permissions).
 */
export function ensureConfigDefaults(): void {
  syncConfigDefaults();
}

export function ensureConfigDir(): void {
  if (!existsSync(CONFIG_DIR)) {
    mkdirSync(CONFIG_DIR, { recursive: true });
  }
  // Initialize bundled docs (creates ~/.origin-relay/docs/ with sources.md, agents.md, permissions.md)
  initializeDocs();

  // Initialize config defaults
  ensureConfigDefaults();

  // Initialize tool icons (CLI tool icons for turn card display)
  ensureToolIcons();
}

export function loadStoredConfig(): StoredConfig | null {
  try {
    if (!existsSync(CONFIG_FILE)) {
      return null;
    }
    const config = readJsonFileSync<StoredConfig>(CONFIG_FILE);

    // Must have workspaces array
    if (!Array.isArray(config.workspaces)) {
      return null;
    }

    // Expand path variables (~ and ${HOME}) for portability
    for (const workspace of config.workspaces) {
      workspace.rootPath = expandPath(workspace.rootPath);
    }

    // Validate active workspace exists
    const activeWorkspace = config.workspaces.find(w => w.id === config.activeWorkspaceId);
    if (!activeWorkspace) {
      // Default to first workspace
      config.activeWorkspaceId = config.workspaces[0]?.id || null;
    }

    return config;
  } catch (error) {
    debug('[config] loadStoredConfig failed:', error instanceof Error ? error.message : error);
    return null;
  }
}

/**
 * Ensure workspace folder structure and default sources/skills exist for the given workspaces.
 * Skips workspaces that have already been ensured in this process (tracked by ensuredWorkspaces set).
 * Call this at app startup and when config.json changes — NOT on every config read.
 */
export function ensureWorkspaceDefaults(workspaces: Workspace[]): void {
  for (const workspace of workspaces) {
    if (ensuredWorkspaces.has(workspace.rootPath)) continue;

    if (!isValidWorkspace(workspace.rootPath)) {
      try {
        createWorkspaceAtPath(workspace.rootPath, workspace.name);
      } catch (wsError) {
        debug('[config] Failed to create workspace at', workspace.rootPath, ':', wsError instanceof Error ? wsError.message : wsError);
      }
    }
    try {
      ensureDefaultSources(workspace.rootPath);
    } catch {
      // Non-critical — don't block startup
    }
    try {
      ensureDefaultSkills(workspace.rootPath);
    } catch {
      // Non-critical — don't block startup
    }

    ensuredWorkspaces.add(workspace.rootPath);
  }
}

/**
 * Reset the ensured workspaces set, so that the next ensureWorkspaceDefaults call
 * will re-check all workspaces. Call this when config.json changes (e.g., new workspace added).
 */
export function resetEnsuredWorkspaces(): void {
  ensuredWorkspaces.clear();
}

// Legacy credential helpers removed - use connection-aware credential lookup instead:
// - getAnthropicApiKey() → credentialManager.getLlmApiKey(connectionSlug)
// - getClaudeOAuthToken() → credentialManager.getLlmOAuth(connectionSlug)

export function saveConfig(config: StoredConfig): void {
  ensureConfigDir();

  // Convert paths to portable form (~ prefix) for cross-machine compatibility
  const storageConfig: StoredConfig = {
    ...config,
    workspaces: config.workspaces.map(ws => ({
      ...ws,
      rootPath: toPortablePath(ws.rootPath),
    })),
  };

  writeFileSync(CONFIG_FILE, JSON.stringify(storageConfig, null, 2), 'utf-8');
}

// Legacy updateApiKey() removed - use setupLlmConnection IPC handler instead.

// Legacy getters/setters removed - use LLM connections instead:
// - getAuthType/setAuthType -> derive from getDefaultLlmConnection()/getLlmConnection()
// - getAnthropicBaseUrl/setAnthropicBaseUrl -> use connection.baseUrl
// - getCustomModel/setCustomModel -> use connection.defaultModel


/**
 * Get whether desktop notifications are enabled.
 * Defaults to true if not set.
 */
export function getNotificationsEnabled(): boolean {
  const config = loadStoredConfig();
  if (config?.notificationsEnabled !== undefined) {
    return config.notificationsEnabled;
  }
  const defaults = loadConfigDefaults();
  return defaults.defaults.notificationsEnabled;
}

/**
 * Set whether desktop notifications are enabled.
 */
export function setNotificationsEnabled(enabled: boolean): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.notificationsEnabled = enabled;
  saveConfig(config);
}

/**
 * Get whether onboarding has been completed.
 * Defaults to false if not set.
 */
export function getOnboardingCompleted(): boolean {
  const config = loadStoredConfig();
  return config?.onboardingCompleted ?? false;
}

/**
 * Get whether the user has verified their email via OTP.
 * Defaults to false if not set.
 */
export function getEmailVerified(): boolean {
  const config = loadStoredConfig();
  return config?.emailVerified ?? false;
}

/**
 * Get the verified email address.
 * Returns null if not verified.
 */
export function getVerifiedEmail(): string | null {
  const config = loadStoredConfig();
  return config?.verifiedEmail ?? null;
}

/**
 * Set the email as verified and persist the verified email address.
 */
export function setEmailVerified(email: string): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.emailVerified = true;
  config.verifiedEmail = email;
  saveConfig(config);
}

/**
 * Get whether auto-capitalisation is enabled.
 * Defaults to true if not set.
 */
export function getAutoCapitalisation(): boolean {
  const config = loadStoredConfig();
  if (config?.autoCapitalisation !== undefined) {
    return config.autoCapitalisation;
  }
  const defaults = loadConfigDefaults();
  return defaults.defaults.autoCapitalisation;
}

/**
 * Set whether auto-capitalisation is enabled.
 */
export function setAutoCapitalisation(enabled: boolean): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.autoCapitalisation = enabled;
  saveConfig(config);
}

/**
 * Get the key combination used to send messages.
 * Defaults to 'enter' if not set.
 */
export function getSendMessageKey(): 'enter' | 'cmd-enter' {
  const config = loadStoredConfig();
  if (config?.sendMessageKey !== undefined) {
    return config.sendMessageKey;
  }
  const defaults = loadConfigDefaults();
  return defaults.defaults.sendMessageKey;
}

/**
 * Set the key combination used to send messages.
 */
export function setSendMessageKey(key: 'enter' | 'cmd-enter'): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.sendMessageKey = key;
  saveConfig(config);
}

/**
 * Get whether spell check is enabled in the input.
 */
export function getSpellCheck(): boolean {
  const config = loadStoredConfig();
  if (config?.spellCheck !== undefined) {
    return config.spellCheck;
  }
  const defaults = loadConfigDefaults();
  return defaults.defaults.spellCheck;
}

/**
 * Set whether spell check is enabled in the input.
 */
export function setSpellCheck(enabled: boolean): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.spellCheck = enabled;
  saveConfig(config);
}

/**
 * Get whether screen should stay awake while sessions are running.
 * Defaults to false if not set.
 */
export function getKeepAwakeWhileRunning(): boolean {
  const config = loadStoredConfig();
  if (config?.keepAwakeWhileRunning !== undefined) {
    return config.keepAwakeWhileRunning;
  }
  const defaults = loadConfigDefaults();
  return defaults.defaults.keepAwakeWhileRunning;
}

/**
 * Set whether screen should stay awake while sessions are running.
 */
export function setKeepAwakeWhileRunning(enabled: boolean): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.keepAwakeWhileRunning = enabled;
  saveConfig(config);
}

/**
 * Get whether rich tool descriptions are enabled.
 * When enabled, all tool calls include intent and display name metadata.
 * Defaults to true if not set.
 */
export function getRichToolDescriptions(): boolean {
  const config = loadStoredConfig();
  if (config?.richToolDescriptions !== undefined) {
    return config.richToolDescriptions;
  }
  return true;
}

/**
 * Set whether rich tool descriptions are enabled.
 */
export function setRichToolDescriptions(enabled: boolean): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.richToolDescriptions = enabled;
  saveConfig(config);
}

/**
 * Get whether remote access server should auto-start on launch.
 * Defaults to false if not set.
 */
export function getRemoteAccessEnabled(): boolean {
  const config = loadStoredConfig();
  return config?.remoteAccessEnabled ?? false;
}

/**
 * Set whether remote access server should auto-start on launch.
 */
export function setRemoteAccessEnabled(enabled: boolean): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.remoteAccessEnabled = enabled;
  saveConfig(config);
}

/**
 * Get the port for the remote access server.
 * Defaults to 9100 if not set.
 */
export function getRemoteAccessPort(): number {
  const config = loadStoredConfig();
  return config?.remoteAccessPort ?? 9100;
}

/**
 * Get persisted Git Bash path (Windows only).
 * Used to set CLAUDE_CODE_GIT_BASH_PATH for the SDK subprocess.
 */
export function getGitBashPath(): string | undefined {
  const config = loadStoredConfig();
  return config?.gitBashPath;
}

/**
 * Set Git Bash path (Windows only).
 * Persists to config so it survives app restarts.
 * Returns false if the config could not be loaded (path not persisted).
 */
export function setGitBashPath(path: string): boolean {
  const config = loadStoredConfig();
  if (!config) {
    console.warn('[storage] Failed to persist Git Bash path: config could not be loaded');
    return false;
  }
  config.gitBashPath = path;
  saveConfig(config);
  return true;
}

/**
 * Clear persisted Git Bash path (Windows only).
 * Used when the stored path is stale or invalid.
 */
export function clearGitBashPath(): void {
  const config = loadStoredConfig();
  if (!config || !config.gitBashPath) return;
  delete config.gitBashPath;
  saveConfig(config);
}

// Note: getDefaultWorkingDirectory/setDefaultWorkingDirectory removed
// Working directory is now stored per-workspace in workspace config.json (defaults.workingDirectory)
// Note: getDefaultPermissionMode/getEnabledPermissionModes removed
// Permission settings are now stored per-workspace in workspace config.json (defaults.permissionMode, defaults.cyclablePermissionModes)

export function getConfigPath(): string {
  return CONFIG_FILE;
}

/**
 * Clear all configuration and credentials (for logout).
 * Deletes config file and credentials file.
 */
export async function clearAllConfig(): Promise<void> {
  // Delete config file
  if (existsSync(CONFIG_FILE)) {
    rmSync(CONFIG_FILE);
  }

  // Delete credentials file
  const credentialsFile = join(CONFIG_DIR, 'credentials.enc');
  if (existsSync(credentialsFile)) {
    rmSync(credentialsFile);
  }

  // Optionally: Delete workspace data (conversations)
  const workspacesDir = join(CONFIG_DIR, 'workspaces');
  if (existsSync(workspacesDir)) {
    rmSync(workspacesDir, { recursive: true });
  }
}

// ============================================
// Workspace Management Functions
// ============================================

/**
 * Generate a unique workspace ID.
 * Uses a random UUID-like format.
 */
export function generateWorkspaceId(): string {
  // Generate random bytes and format as UUID-like string (8-4-4-4-12)
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Find workspace icon file at workspace_root/icon.*
 * Returns absolute path to icon file if found, null otherwise
 */
export function findWorkspaceIcon(rootPath: string): string | null {
  return findIconFile(rootPath) ?? null;
}

export function getWorkspaces(): Workspace[] {
  const config = loadStoredConfig();
  const workspaces = config?.workspaces || [];

  // Resolve workspace names from folder config and local icons
  return workspaces.map(w => {
    // Read name from workspace folder config (single source of truth)
    const wsConfig = loadWorkspaceConfig(w.rootPath);
    const name = wsConfig?.name || basename(w.rootPath) || 'Untitled';

    // If workspace has a stored iconUrl that's a remote URL, use it
    // Otherwise check for local icon file
    let iconUrl = w.iconUrl;
    if (!iconUrl || (!iconUrl.startsWith('http://') && !iconUrl.startsWith('https://'))) {
      const localIcon = findWorkspaceIcon(w.rootPath);
      if (localIcon) {
        // Convert absolute path to file:// URL for Electron renderer
        // Append mtime as cache-buster so UI refreshes when icon changes
        try {
          const mtime = statSync(localIcon).mtimeMs;
          iconUrl = `file://${localIcon}?t=${mtime}`;
        } catch {
          iconUrl = `file://${localIcon}`;
        }
      }
    }

    return { ...w, name, iconUrl };
  });
}

/**
 * Resolve a workspace's absolute root path on disk from its id.
 *
 * Workspace ids are UUIDs but the on-disk folder is the configured `rootPath`,
 * which may live anywhere (e.g. ~/.origin-relay-headless/workspaces/origin-financial).
 * Returns null if the workspace is unknown.
 *
 * Use this whenever you have a workspace id and need the folder containing
 * `sources/`, `sessions/`, `skills/`, etc. Do NOT join CONFIG_DIR + id manually.
 */
export function getWorkspaceRootPath(workspaceId: string): string | null {
  const config = loadStoredConfig();
  const ws = config?.workspaces.find(w => w.id === workspaceId);
  if (!ws) return null;
  // rootPath may be stored in portable form with leading '~' for $HOME.
  return expandPath(ws.rootPath);
}

export function getActiveWorkspace(): Workspace | null {
  const config = loadStoredConfig();
  if (!config || !config.activeWorkspaceId) {
    return config?.workspaces[0] || null;
  }
  return config.workspaces.find(w => w.id === config.activeWorkspaceId) || config.workspaces[0] || null;
}

/**
 * Find a workspace by ID, name (case-insensitive), or folder name.
 * Useful for CLI -w flag to specify workspace.
 * Folder-name matching is needed because source loading uses basename(rootPath)
 * as workspaceId for credential lookups.
 */
export function getWorkspaceByNameOrId(nameOrId: string): Workspace | null {
  const workspaces = getWorkspaces();
  return workspaces.find(w =>
    w.id === nameOrId ||
    w.name.toLowerCase() === nameOrId.toLowerCase() ||
    basename(w.rootPath) === nameOrId
  ) || null;
}

export function setActiveWorkspace(workspaceId: string): void {
  const config = loadStoredConfig();
  if (!config) return;

  const workspace = config.workspaces.find(w => w.id === workspaceId);
  if (!workspace) return;

  config.activeWorkspaceId = workspaceId;
  saveConfig(config);
}

/**
 * Atomically switch to a workspace and load/create a session.
 * This prevents race conditions by doing both operations together.
 *
 * @param workspaceId The ID of the workspace to switch to
 * @returns The workspace and session, or null if workspace not found
 */
export async function switchWorkspaceAtomic(workspaceId: string): Promise<{ workspace: Workspace; session: SessionConfig } | null> {
  const config = loadStoredConfig();
  if (!config) return null;

  const workspace = config.workspaces.find(w => w.id === workspaceId);
  if (!workspace) return null;

  // Get or create the latest session for this workspace
  const session = await getOrCreateLatestSession(workspace.rootPath);

  // Update active workspace in config
  config.activeWorkspaceId = workspaceId;
  workspace.lastAccessedAt = Date.now();
  saveConfig(config);

  return { workspace, session };
}

/**
 * Add a workspace to the global config.
 * @param workspace - Workspace data (must include rootPath)
 */
export function addWorkspace(workspace: Omit<Workspace, 'id' | 'createdAt'>): Workspace {
  const config = loadStoredConfig();
  if (!config) {
    throw new Error('No config found');
  }

  // Check if workspace with same rootPath already exists
  const existing = config.workspaces.find(w => w.rootPath === workspace.rootPath);
  if (existing) {
    // Update existing workspace with new settings
    const updated: Workspace = {
      ...existing,
      ...workspace,
      id: existing.id,
      createdAt: existing.createdAt,
    };
    const existingIndex = config.workspaces.indexOf(existing);
    config.workspaces[existingIndex] = updated;
    saveConfig(config);
    return updated;
  }

  const newWorkspace: Workspace = {
    ...workspace,
    id: generateWorkspaceId(),
    createdAt: Date.now(),
  };

  // Create workspace folder structure if it doesn't exist
  if (!isValidWorkspace(newWorkspace.rootPath)) {
    createWorkspaceAtPath(newWorkspace.rootPath, newWorkspace.name);
  }

  // Ensure default sources are installed
  try {
    ensureDefaultSources(newWorkspace.rootPath);
  } catch {
    // Non-critical
  }

  // Ensure default skills are installed
  try {
    ensureDefaultSkills(newWorkspace.rootPath);
  } catch {
    // Non-critical
  }

  config.workspaces.push(newWorkspace);

  // If this is the only workspace, make it active
  if (config.workspaces.length === 1) {
    config.activeWorkspaceId = newWorkspace.id;
  }

  saveConfig(config);
  return newWorkspace;
}

/**
 * Sync workspaces by discovering workspaces in the default location
 * that aren't already tracked in the global config.
 * Call this on app startup.
 */
export function syncWorkspaces(): void {
  const config = loadStoredConfig();
  if (!config) return;

  const discoveredPaths = discoverWorkspacesInDefaultLocation();
  const trackedPaths = new Set(config.workspaces.map(w => w.rootPath));

  let added = false;
  for (const rootPath of discoveredPaths) {
    if (trackedPaths.has(rootPath)) continue;

    // Load the workspace config to get name
    const wsConfig = loadWorkspaceConfig(rootPath);
    if (!wsConfig) continue;

    const newWorkspace: Workspace = {
      id: wsConfig.id || generateWorkspaceId(),
      name: wsConfig.name,
      rootPath,
      createdAt: wsConfig.createdAt || Date.now(),
    };

    config.workspaces.push(newWorkspace);
    added = true;
  }

  if (added) {
    // If no active workspace, set to first
    if (!config.activeWorkspaceId && config.workspaces.length > 0) {
      config.activeWorkspaceId = config.workspaces[0]!.id;
    }
    saveConfig(config);
  }
}

export async function removeWorkspace(workspaceId: string): Promise<boolean> {
  const config = loadStoredConfig();
  if (!config) return false;

  const index = config.workspaces.findIndex(w => w.id === workspaceId);
  if (index === -1) return false;

  config.workspaces.splice(index, 1);

  // If we removed the active workspace, switch to first available
  if (config.activeWorkspaceId === workspaceId) {
    config.activeWorkspaceId = config.workspaces[0]?.id || null;
  }

  saveConfig(config);

  // Clean up credential store credentials for this workspace
  const manager = getCredentialManager();
  await manager.deleteWorkspaceCredentials(workspaceId);

  // Delete workspace data directory (sessions, plans, etc.)
  const workspaceDataDir = join(WORKSPACES_DIR, workspaceId);
  if (existsSync(workspaceDataDir)) {
    try {
      rmSync(workspaceDataDir, { recursive: true });
    } catch (error) {
      console.error(`[storage] Failed to delete workspace data directory: ${workspaceDataDir}`, error);
    }
  }

  return true;
}

// Note: renameWorkspace() was removed - workspace names are now stored only in folder config
// Use updateWorkspaceSetting('name', ...) to rename workspaces via the folder config

// ============================================
// Workspace Conversation Persistence
// ============================================

const WORKSPACES_DIR = join(CONFIG_DIR, 'workspaces');

function ensureWorkspaceDir(workspaceId: string): string {
  const dir = join(WORKSPACES_DIR, workspaceId);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true });
  }
  return dir;
}


// Re-export types from core for convenience
export type { StoredAttachment, StoredMessage } from '@craft-agent/core/types';

export interface WorkspaceConversation {
  messages: StoredMessage[];
  tokenUsage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    contextTokens: number;
    costUsd: number;
    cacheReadTokens?: number;
    cacheCreationTokens?: number;
  };
  savedAt: number;
}

// Save workspace conversation (messages + token usage)
export function saveWorkspaceConversation(
  workspaceId: string,
  messages: StoredMessage[],
  tokenUsage: WorkspaceConversation['tokenUsage']
): void {
  const dir = ensureWorkspaceDir(workspaceId);
  const filePath = join(dir, 'conversation.json');

  const conversation: WorkspaceConversation = {
    messages,
    tokenUsage,
    savedAt: Date.now(),
  };

  try {
    writeFileSync(filePath, JSON.stringify(conversation, null, 2), 'utf-8');
  } catch (e) {
    // Handle cyclic structures or other serialization errors
    console.error(`[storage] [CYCLIC STRUCTURE] Failed to save workspace conversation:`, e);
    console.error(`[storage] Message count: ${messages.length}, message types: ${messages.map(m => m.type).join(', ')}`);
    // Try to save with sanitized messages
    try {
      const sanitizedMessages = messages.map((m, i) => {
        let safeToolInput = m.toolInput;
        if (m.toolInput) {
          try {
            JSON.stringify(m.toolInput);
          } catch (inputErr) {
            console.error(`[storage] [CYCLIC STRUCTURE] in message ${i} toolInput (tool: ${m.toolName}), keys: ${Object.keys(m.toolInput).join(', ')}, error: ${inputErr}`);
            safeToolInput = { error: '[non-serializable input]' };
          }
        }
        return { ...m, toolInput: safeToolInput };
      });
      const sanitizedConversation: WorkspaceConversation = {
        messages: sanitizedMessages,
        tokenUsage,
        savedAt: Date.now(),
      };
      writeFileSync(filePath, JSON.stringify(sanitizedConversation, null, 2), 'utf-8');
      console.error(`[storage] Saved sanitized workspace conversation successfully`);
    } catch (e2) {
      console.error(`[storage] Failed to save even sanitized workspace conversation:`, e2);
    }
  }
}

// Load workspace conversation
export function loadWorkspaceConversation(workspaceId: string): WorkspaceConversation | null {
  const filePath = join(WORKSPACES_DIR, workspaceId, 'conversation.json');

  try {
    if (!existsSync(filePath)) {
      return null;
    }
    return readJsonFileSync<WorkspaceConversation>(filePath);
  } catch {
    return null;
  }
}

// Get workspace data directory path
export function getWorkspaceDataPath(workspaceId: string): string {
  return join(WORKSPACES_DIR, workspaceId);
}

// Clear workspace conversation
export function clearWorkspaceConversation(workspaceId: string): void {
  const filePath = join(WORKSPACES_DIR, workspaceId, 'conversation.json');
  if (existsSync(filePath)) {
    writeFileSync(filePath, '{}', 'utf-8');
  }

  // Also clear any active plan (plans are session-scoped)
  clearWorkspacePlan(workspaceId);
}

// ============================================
// Plan Storage (Session-Scoped)
// Plans are stored per-workspace and cleared with /clear
// ============================================

/**
 * Save a plan for a workspace.
 * Plans are session-scoped - they persist during the session but are
 * cleared when the user runs /clear or starts a new session.
 */
export function saveWorkspacePlan(workspaceId: string, plan: Plan): void {
  const dir = ensureWorkspaceDir(workspaceId);
  const filePath = join(dir, 'plan.json');
  writeFileSync(filePath, JSON.stringify(plan, null, 2), 'utf-8');
}

/**
 * Load the current plan for a workspace.
 * Returns null if no plan exists.
 */
export function loadWorkspacePlan(workspaceId: string): Plan | null {
  const filePath = join(WORKSPACES_DIR, workspaceId, 'plan.json');

  try {
    if (!existsSync(filePath)) {
      return null;
    }
    return readJsonFileSync<Plan>(filePath);
  } catch {
    return null;
  }
}

/**
 * Clear the plan for a workspace.
 * Called when user runs /clear or cancels a plan.
 */
export function clearWorkspacePlan(workspaceId: string): void {
  const filePath = join(WORKSPACES_DIR, workspaceId, 'plan.json');
  if (existsSync(filePath)) {
    rmSync(filePath);
  }
}

// ============================================
// Session Input Drafts
// Persists input text per session across app restarts
// ============================================

const DRAFTS_FILE = join(CONFIG_DIR, 'drafts.json');

interface DraftsData {
  drafts: Record<string, string>;
  updatedAt: number;
}

/**
 * Load all drafts from disk
 */
function loadDraftsData(): DraftsData {
  try {
    if (!existsSync(DRAFTS_FILE)) {
      return { drafts: {}, updatedAt: 0 };
    }
    return readJsonFileSync<DraftsData>(DRAFTS_FILE);
  } catch {
    return { drafts: {}, updatedAt: 0 };
  }
}

/**
 * Save drafts to disk
 */
function saveDraftsData(data: DraftsData): void {
  ensureConfigDir();
  data.updatedAt = Date.now();
  writeFileSync(DRAFTS_FILE, JSON.stringify(data, null, 2), 'utf-8');
}

/**
 * Get draft text for a session
 */
export function getSessionDraft(sessionId: string): string | null {
  const data = loadDraftsData();
  return data.drafts[sessionId] ?? null;
}

/**
 * Set draft text for a session
 * Pass empty string to clear the draft
 */
export function setSessionDraft(sessionId: string, text: string): void {
  const data = loadDraftsData();
  if (text) {
    data.drafts[sessionId] = text;
  } else {
    delete data.drafts[sessionId];
  }
  saveDraftsData(data);
}

/**
 * Delete draft for a session
 */
export function deleteSessionDraft(sessionId: string): void {
  const data = loadDraftsData();
  delete data.drafts[sessionId];
  saveDraftsData(data);
}

/**
 * Get all drafts as a record
 */
export function getAllSessionDrafts(): Record<string, string> {
  const data = loadDraftsData();
  return data.drafts;
}

// ============================================
// Theme Storage (App-level only)
// ============================================

import type { ThemeOverrides, ThemeFile, PresetTheme } from './theme.ts';
import { readdirSync } from 'fs';

const APP_THEME_FILE = join(CONFIG_DIR, 'theme.json');
const APP_THEMES_DIR = join(CONFIG_DIR, 'themes');

/**
 * Get the path to the app-level theme override file (~/.origin-relay/theme.json).
 */
export function getAppThemePath(): string {
  return APP_THEME_FILE;
}

// Track if preset themes have been synced this session (prevents re-init on hot reload)
let presetsInitialized = false;

/**
 * Get the app-level themes directory.
 * Preset themes are stored at ~/.origin-relay/themes/
 */
export function getAppThemesDir(): string {
  return APP_THEMES_DIR;
}

/**
 * Load app-level theme overrides
 */
export function loadAppTheme(): ThemeOverrides | null {
  try {
    if (!existsSync(APP_THEME_FILE)) {
      return null;
    }
    return readJsonFileSync<ThemeOverrides>(APP_THEME_FILE);
  } catch {
    return null;
  }
}

/**
 * Save app-level theme overrides
 */
export function saveAppTheme(theme: ThemeOverrides): void {
  ensureConfigDir();
  writeFileSync(APP_THEME_FILE, JSON.stringify(theme, null, 2), 'utf-8');
}


// ============================================
// Preset Themes (app-level)
// ============================================

/**
 * Sync bundled preset themes to disk on launch.
 * Preserves user customizations:
 * - If file doesn't exist → copy from bundle
 * - If file exists but is invalid/corrupt → copy from bundle (auto-heal)
 * - If file exists and is valid → skip (preserve user changes)
 *
 * User-created custom theme files (with non-bundled filenames) are untouched.
 * User color overrides live in theme.json (separate file) and are never touched.
 */
export function ensurePresetThemes(): void {
  // Skip if already initialized this session (prevents re-init on hot reload)
  if (presetsInitialized) {
    return;
  }
  presetsInitialized = true;

  const themesDir = getAppThemesDir();

  // Create themes directory if it doesn't exist
  if (!existsSync(themesDir)) {
    mkdirSync(themesDir, { recursive: true });
  }

  // Resolve bundled themes directory via shared asset resolver
  const bundledThemesDir = getBundledAssetsDir('themes');
  if (!bundledThemesDir) {
    return;
  }

  // Copy bundled preset themes to disk, preserving user customizations.
  // - If file doesn't exist → copy from bundle
  // - If file exists but is invalid/corrupt → copy from bundle (auto-heal)
  // - If file exists and is valid → skip (preserve user changes)
  try {
    const bundledFiles = readdirSync(bundledThemesDir).filter(f => f.endsWith('.json'));
    for (const file of bundledFiles) {
      const srcPath = join(bundledThemesDir, file);
      const destPath = join(themesDir, file);

      // Skip if file exists and is valid (preserve user customizations)
      if (existsSync(destPath) && isValidThemeFile(destPath)) {
        continue;
      }

      // Copy from bundle (new file or auto-heal corrupt file)
      const content = readFileSync(srcPath, 'utf-8');
      writeFileSync(destPath, content, 'utf-8');
    }
  } catch {
    // Ignore errors - themes are optional
  }
}

/**
 * Load all preset themes from app themes directory.
 * Returns array of PresetTheme objects sorted by name.
 */
export function loadPresetThemes(): PresetTheme[] {
  ensurePresetThemes();

  const themesDir = getAppThemesDir();
  if (!existsSync(themesDir)) {
    return [];
  }

  const themes: PresetTheme[] = [];

  try {
    const files = readdirSync(themesDir).filter(f => f.endsWith('.json'));
    for (const file of files) {
      const id = file.replace('.json', '');
      const path = join(themesDir, file);
      try {
        const theme = readJsonFileSync<ThemeFile>(path);
        // Resolve relative backgroundImage paths to file:// URLs
        const resolvedTheme = resolveThemeBackgroundImage(theme, path);
        themes.push({ id, path, theme: resolvedTheme });
      } catch {
        // Skip invalid theme files
      }
    }
  } catch {
    return [];
  }

  // Sort by name (default first, then alphabetically)
  return themes.sort((a, b) => {
    if (a.id === 'default') return -1;
    if (b.id === 'default') return 1;
    return (a.theme.name || a.id).localeCompare(b.theme.name || b.id);
  });
}

/**
 * Get MIME type from file extension for data URL encoding.
 */
function getMimeType(filePath: string): string {
  const ext = filePath.toLowerCase().split('.').pop();
  switch (ext) {
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    case 'svg': return 'image/svg+xml';
    default: return 'application/octet-stream';
  }
}

/**
 * Resolve relative backgroundImage paths to data URLs.
 * If the backgroundImage is a relative path (no protocol), resolve it relative to the theme's directory,
 * read the file, and convert it to a data URL. This is necessary because the renderer process
 * cannot access file:// URLs directly when running on localhost in dev mode.
 * @param theme - Theme object to process
 * @param themePath - Absolute path to the theme's JSON file
 */
function resolveThemeBackgroundImage(theme: ThemeFile, themePath: string): ThemeFile {
  if (!theme.backgroundImage) {
    return theme;
  }

  // Check if it's already an absolute URL (has protocol like http://, https://, data:)
  const hasProtocol = /^[a-z][a-z0-9+.-]*:/i.test(theme.backgroundImage);
  if (hasProtocol) {
    return theme;
  }

  // It's a relative path - resolve it relative to the theme's directory
  const themeDir = dirname(themePath);
  const absoluteImagePath = join(themeDir, theme.backgroundImage);

  // Read the file and convert to data URL so renderer can use it
  // (file:// URLs are blocked in renderer when running on localhost)
  try {
    if (!existsSync(absoluteImagePath)) {
      console.warn(`Theme background image not found: ${absoluteImagePath}`);
      return theme;
    }

    const imageBuffer = readFileSync(absoluteImagePath);
    const base64 = imageBuffer.toString('base64');
    const mimeType = getMimeType(absoluteImagePath);
    const dataUrl = `data:${mimeType};base64,${base64}`;

    return {
      ...theme,
      backgroundImage: dataUrl,
    };
  } catch (error) {
    console.warn(`Failed to read theme background image: ${absoluteImagePath}`, error);
    return theme;
  }
}

/**
 * Load a specific preset theme by ID.
 * @param id - Theme ID (filename without .json)
 */
export function loadPresetTheme(id: string): PresetTheme | null {
  const themesDir = getAppThemesDir();
  const path = join(themesDir, `${id}.json`);

  if (!existsSync(path)) {
    return null;
  }

  try {
    const theme = readJsonFileSync<ThemeFile>(path);
    // Resolve relative backgroundImage paths to file:// URLs
    const resolvedTheme = resolveThemeBackgroundImage(theme, path);
    return { id, path, theme: resolvedTheme };
  } catch {
    return null;
  }
}

/**
 * Get the path to the app-level preset themes directory.
 */
export function getPresetThemesDir(): string {
  return getAppThemesDir();
}

/**
 * Reset a preset theme to its bundled default.
 * Copies the bundled version over the user's version.
 * Resolves bundled path automatically via getBundledAssetsDir('themes').
 * @param id - Theme ID to reset
 */
export function resetPresetTheme(id: string): boolean {
  // Resolve bundled themes directory via shared asset resolver
  const bundledThemesDir = getBundledAssetsDir('themes');
  if (!bundledThemesDir) {
    return false;
  }

  const bundledPath = join(bundledThemesDir, `${id}.json`);
  const themesDir = getAppThemesDir();
  const destPath = join(themesDir, `${id}.json`);

  if (!existsSync(bundledPath)) {
    return false;
  }

  try {
    const content = readFileSync(bundledPath, 'utf-8');
    if (!existsSync(themesDir)) {
      mkdirSync(themesDir, { recursive: true });
    }
    writeFileSync(destPath, content, 'utf-8');
    return true;
  } catch {
    return false;
  }
}

// ============================================
// Color Theme Selection (stored in config)
// ============================================

/**
 * Get the currently selected color theme ID.
 * Returns 'default' if not set.
 */
export function getColorTheme(): string {
  const config = loadStoredConfig();
  if (config?.colorTheme !== undefined) {
    return config.colorTheme;
  }
  const defaults = loadConfigDefaults();
  return defaults.defaults.colorTheme;
}

/**
 * Set the color theme ID.
 */
export function setColorTheme(themeId: string): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.colorTheme = themeId;
  saveConfig(config);
}

// ============================================
// Auto-Update Dismissed Version
// ============================================

/**
 * Get the dismissed update version.
 * Returns null if no version is dismissed.
 */
export function getDismissedUpdateVersion(): string | null {
  const config = loadStoredConfig();
  return config?.dismissedUpdateVersion ?? null;
}

/**
 * Set the dismissed update version.
 * Pass the version string to dismiss notifications for that version.
 */
export function setDismissedUpdateVersion(version: string): void {
  const config = loadStoredConfig();
  if (!config) return;
  config.dismissedUpdateVersion = version;
  saveConfig(config);
}

/**
 * Clear the dismissed update version.
 * Call this when a new version is released (or on successful update).
 */
export function clearDismissedUpdateVersion(): void {
  const config = loadStoredConfig();
  if (!config) return;
  delete config.dismissedUpdateVersion;
  saveConfig(config);
}

// ============================================
// LLM Connections
// ============================================

// Re-export types for convenience (imports are at top of file)
export type {
  LlmConnection,
  LlmProviderType,
  LlmAuthType,
  LlmConnectionWithStatus,
} from './llm-connections.ts';

/**
 * Migrate Codex (OpenAI) and Copilot connections to Pi backend.
 * Runs on startup — transparently routes existing users through PiAgent.
 *
 * No re-auth needed: credentials are keyed by connection slug (not provider),
 * and PiAgent reads the same OAuth tokens via piAuthProvider.
 *
 * Migration rules:
 * - openai + oauth       → pi + openai-codex
 * - openai + api_key     → pi + openai
 * - openai_compat        → pi + openai  (keep baseUrl)
 * - copilot              → pi + github-copilot
 * - defaultModel reset to Pi's default (stale Codex/Copilot model IDs dropped)
 * - codexPath removed (no longer needed)
 */
function migrateCodexCopilotToPi(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;
  let changed = false;

  for (const connection of config.llmConnections) {
    // Cast to string for legacy providerType values that were removed from LlmProviderType
    // but may still exist on disk in old configs. Cast to any for legacy codexPath field.
    const providerStr = connection.providerType as string;
    const connAny = connection as any;
    if (providerStr === 'openai' && connection.authType === 'oauth') {
      connection.providerType = 'pi';
      connection.piAuthProvider = 'openai-codex';
      connection.name = 'ChatGPT Plus (via Pi)';
      delete connAny.codexPath;
      connection.defaultModel = undefined; // reset — backfill picks Pi default
      connection.models = undefined;
      changed = true;
    } else if (providerStr === 'openai' && (connection.authType === 'api_key' || connection.authType === 'api_key_with_endpoint')) {
      connection.providerType = 'pi';
      connection.piAuthProvider = 'openai';
      connection.name = 'OpenAI API (via Pi)';
      delete connAny.codexPath;
      connection.defaultModel = undefined;
      connection.models = undefined;
      changed = true;
    } else if (providerStr === 'openai_compat') {
      connection.providerType = 'pi';
      connection.piAuthProvider = 'openai';
      // keep baseUrl for custom endpoints
      delete connAny.codexPath;
      connection.defaultModel = undefined;
      connection.models = undefined;
      changed = true;
    } else if (providerStr === 'copilot') {
      connection.providerType = 'pi';
      connection.piAuthProvider = 'github-copilot';
      connection.name = 'GitHub Copilot (via Pi)';
      delete connAny.codexPath;
      connection.defaultModel = undefined;
      connection.models = undefined;
      changed = true;
    }
  }

  // Clean up openaiVariant config field (Codex-specific A/B testing, no longer relevant)
  const configAny = config as any;
  if (configAny.openaiVariant) {
    delete configAny.openaiVariant;
    changed = true;
  }

  return changed;
}

/**
 * Backfill models and defaultModel on ALL connections.
 * Ensures built-in connections (anthropic, openai) always have models populated,
 * not just compat connections.
 */
export function shouldMigratePiOpenAiProvider(connection: Pick<LlmConnection, 'providerType' | 'piAuthProvider' | 'authType' | 'baseUrl'>): boolean {
  // Legacy cleanup: old ChatGPT Plus OAuth connections may still be tagged as `openai`.
  // Only migrate those to `openai-codex`.
  //
  // IMPORTANT: Do NOT migrate API-key or custom-endpoint connections:
  // - `api_key` / `api_key_with_endpoint` with `openai` must remain regular OpenAI API auth.
  // - forcing them to `openai-codex` routes requests to ChatGPT backend auth and breaks on restart.
  if (!isPiProvider(connection.providerType)) return false;
  if (connection.piAuthProvider !== 'openai') return false;
  if (connection.authType !== 'oauth') return false;
  if (typeof connection.baseUrl === 'string' && connection.baseUrl.trim().length > 0) return false;
  return true;
}

export function shouldRepairPiApiKeyCodexProvider(connection: Pick<LlmConnection, 'providerType' | 'piAuthProvider' | 'authType'>): boolean {
  // Repair broken state from previous startup migrations:
  // API-key connections tagged as `openai-codex` try ChatGPT backend JWT auth and fail.
  if (!isPiProvider(connection.providerType)) return false;
  if (connection.piAuthProvider !== 'openai-codex') return false;
  return connection.authType === 'api_key' || connection.authType === 'api_key_with_endpoint';
}

function normalizeModelIds(models?: Array<{ id: string } | string>): string[] {
  if (!models) return [];
  return models
    .map(m => typeof m === 'string' ? m : m.id)
    .filter((id): id is string => !!id && id.trim().length > 0);
}

function modelSetEquals(a: string[], b: string[]): boolean {
  const as = new Set(a);
  const bs = new Set(b);
  if (as.size !== bs.size) return false;
  for (const id of as) {
    if (!bs.has(id)) return false;
  }
  return true;
}

export function inferModelSelectionMode(
  connection: Pick<LlmConnection, 'models'>,
  providerDefaultModelIds: string[],
): 'automaticallySyncedFromProvider' | 'userDefined3Tier' {
  const currentIds = normalizeModelIds(connection.models);
  if (currentIds.length === 0) return 'automaticallySyncedFromProvider';
  return modelSetEquals(currentIds, providerDefaultModelIds)
    ? 'automaticallySyncedFromProvider'
    : 'userDefined3Tier';
}

function backfillAllConnectionModels(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;
  let changed = false;
  for (const connection of config.llmConnections) {
    // Managed connections may intentionally reference models newer than the
    // pinned static registry. Their curated definitions are the source of
    // truth and must not be replaced with an older provider model list.
    if (isManagedLlmConnectionSlug(connection.slug)) continue;

    // Repair previously broken API-key migration first.
    if (shouldRepairPiApiKeyCodexProvider(connection)) {
      connection.piAuthProvider = 'openai';
      changed = true;
    }

    // Migrate only legacy OAuth-backed Pi OpenAI connections to ChatGPT backend provider key.
    if (shouldMigratePiOpenAiProvider(connection)) {
      connection.piAuthProvider = 'openai-codex';
      changed = true;
    }

    const defaultModels = getDefaultModelsForConnection(connection.providerType, connection.piAuthProvider);
    const defaultModel = getDefaultModelForConnection(connection.providerType, connection.piAuthProvider);
    const providerDefaultModelIds = normalizeModelIds(defaultModels as Array<{ id: string } | string>);

    if (isPiProvider(connection.providerType) && connection.piAuthProvider) {
      const mode = connection.modelSelectionMode
        ?? inferModelSelectionMode(connection, providerDefaultModelIds);
      if (connection.modelSelectionMode !== mode) {
        debug('[storage] backfill mode inferred', {
          slug: connection.slug,
          piAuthProvider: connection.piAuthProvider,
          from: connection.modelSelectionMode,
          to: mode,
          currentModelCount: normalizeModelIds(connection.models).length,
        });
        connection.modelSelectionMode = mode;
        changed = true;
      }

      if (mode === 'automaticallySyncedFromProvider') {
        const currentIds = normalizeModelIds(connection.models);
        if (providerDefaultModelIds.length > 0 && !modelSetEquals(currentIds, providerDefaultModelIds)) {
          connection.models = defaultModels;
          changed = true;
        }
      } else {
        const currentIds = normalizeModelIds(connection.models);
        if (providerDefaultModelIds.length > 0) {
          const allowedIds = new Set(providerDefaultModelIds);
          const canonicalCurrentIds = currentIds.map((id) => {
            if (allowedIds.has(id)) return id;
            if (!id.startsWith('pi/')) {
              const prefixed = `pi/${id}`;
              if (allowedIds.has(prefixed)) return prefixed;
            }
            return id;
          });
          const filtered = canonicalCurrentIds.filter(id => allowedIds.has(id));

          if (!modelSetEquals(canonicalCurrentIds, currentIds) || filtered.length !== currentIds.length) {
            debug('[storage] backfill userDefined filtered', {
              slug: connection.slug,
              piAuthProvider: connection.piAuthProvider,
              beforeCount: currentIds.length,
              canonicalCount: canonicalCurrentIds.length,
              afterCount: filtered.length,
              beforeFirst5: currentIds.slice(0, 5),
              afterFirst5: filtered.slice(0, 5),
            });
            connection.models = filtered;
            changed = true;
          }

          if (filtered.length === 0) {
            debug('[storage] backfill userDefined fallback-to-defaults', {
              slug: connection.slug,
              piAuthProvider: connection.piAuthProvider,
              defaultCount: providerDefaultModelIds.length,
            });
            connection.models = defaultModels;
            changed = true;
          }
        }
      }
    }

    if (defaultModels.length > 0 && (!connection.models || (Array.isArray(connection.models) && connection.models.length === 0))) {
      connection.models = defaultModels;
      changed = true;
    }

    // For Pi connections with piAuthProvider in auto-sync mode, re-filter models
    // to ensure only models matching the auth provider are shown. This migrates
    // existing connections that were created before provider-based filtering.
    // Skip userDefined3Tier — those were already filtered/normalized above.
    if (isPiProvider(connection.providerType) && connection.piAuthProvider && connection.models
        && connection.modelSelectionMode !== 'userDefined3Tier') {
      const correctIds = new Set(
        (defaultModels as Array<{ id: string } | string>).map(m => typeof m === 'string' ? m : m.id)
      );
      const currentIds = new Set(
        connection.models.map(m => typeof m === 'string' ? m : (m as { id: string }).id)
      );
      const mismatch = currentIds.size !== correctIds.size
        || [...currentIds].some(id => !correctIds.has(id))
        || [...correctIds].some(id => !currentIds.has(id));
      if (mismatch) {
        connection.models = defaultModels;
        changed = true;
      }
    }

    if (!connection.defaultModel && defaultModel) {
      connection.defaultModel = defaultModel;
      changed = true;
    }

    // Validate that existing defaultModel is in the models list
    if (connection.defaultModel && connection.models && Array.isArray(connection.models) && connection.models.length > 0) {
      const modelIds = connection.models.map(m => typeof m === 'string' ? m : m.id);
      if (!modelIds.includes(connection.defaultModel)) {
        // Reset to first available model in the list
        const firstModelId = modelIds[0];
        if (firstModelId) {
          connection.defaultModel = firstModelId;
        }
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Migrate Opus 4.5 to Opus 4.6 for direct Anthropic connections (API key or OAuth).
 * Only applies to anthropic provider type (not compat), as third-party providers
 * like OpenRouter may not support the new model ID yet.
 */
function migrateOpus45ToOpus46(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const OPUS_45_ID = 'claude-opus-4-5-20251101';
  const OPUS_46_ID = 'claude-opus-4-6';

  let changed = false;

  for (const connection of config.llmConnections) {
    // Only migrate direct Anthropic connections (not compat/third-party)
    if (connection.providerType !== 'anthropic') continue;

    // Migrate defaultModel
    if (connection.defaultModel === OPUS_45_ID) {
      connection.defaultModel = OPUS_46_ID;
      changed = true;
    }

    // Migrate models array
    if (connection.models && Array.isArray(connection.models)) {
      const hasNew = connection.models.some(m =>
        (typeof m === 'string' ? m : m.id) === OPUS_46_ID
      );

      if (hasNew) {
        // New model already exists — just remove the old entry to avoid duplicates
        const before = connection.models.length;
        connection.models = connection.models.filter(m =>
          (typeof m === 'string' ? m : m.id) !== OPUS_45_ID
        );
        if (connection.models.length !== before) changed = true;
      } else {
        // New model doesn't exist — rename the old entry in place
        for (let i = 0; i < connection.models.length; i++) {
          const model = connection.models[i];
          if (typeof model === 'string' && model === OPUS_45_ID) {
            connection.models[i] = OPUS_46_ID;
            changed = true;
          } else if (typeof model === 'object' && model.id === OPUS_45_ID) {
            model.id = OPUS_46_ID;
            if (model.name?.includes('4.5')) {
              model.name = model.name.replace('4.5', '4.6');
            }
            changed = true;
          }
        }
      }
    }
  }

  return changed;
}

/**
 * Migrate Sonnet 4.5 to Sonnet 4.6 for direct Anthropic connections.
 * Same pattern as migrateOpus45ToOpus46 — updates stored model IDs and names.
 */
function migrateSonnet45ToSonnet46(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const SONNET_45_ID = 'claude-sonnet-4-5-20250929';
  const SONNET_46_ID = 'claude-sonnet-4-6';

  let changed = false;

  for (const connection of config.llmConnections) {
    // Only migrate direct Anthropic connections (not compat/third-party)
    if (connection.providerType !== 'anthropic') continue;

    // Migrate defaultModel
    if (connection.defaultModel === SONNET_45_ID) {
      connection.defaultModel = SONNET_46_ID;
      changed = true;
    }

    // Migrate models array
    if (connection.models && Array.isArray(connection.models)) {
      const hasNew = connection.models.some(m =>
        (typeof m === 'string' ? m : m.id) === SONNET_46_ID
      );

      if (hasNew) {
        // New model already exists — just remove the old entry to avoid duplicates
        const before = connection.models.length;
        connection.models = connection.models.filter(m =>
          (typeof m === 'string' ? m : m.id) !== SONNET_45_ID
        );
        if (connection.models.length !== before) changed = true;
      } else {
        // New model doesn't exist — rename the old entry in place
        for (let i = 0; i < connection.models.length; i++) {
          const model = connection.models[i];
          if (typeof model === 'string' && model === SONNET_45_ID) {
            connection.models[i] = SONNET_46_ID;
            changed = true;
          } else if (typeof model === 'object' && model.id === SONNET_45_ID) {
            model.id = SONNET_46_ID;
            if (model.name?.includes('4.5')) {
              model.name = model.name.replace('4.5', '4.6');
            }
            changed = true;
          }
        }
      }
    }
  }

  return changed;
}

/**
 * Migrate Sonnet 4.5 to Sonnet 4.6 in workspace default models.
 */
function migrateWorkspaceSonnet45ToSonnet46(config: StoredConfig): void {
  if (!config.workspaces) return;

  const SONNET_45_ID = 'claude-sonnet-4-5-20250929';
  const SONNET_46_ID = 'claude-sonnet-4-6';

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;

    if (wsConfig.defaults.model === SONNET_45_ID) {
      wsConfig.defaults.model = SONNET_46_ID;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Migrate Opus 4.5 to Opus 4.6 in workspace default models.
 * Iterates over all workspaces and updates defaults.model if it's Opus 4.5.
 */
function migrateWorkspaceOpus45ToOpus46(config: StoredConfig): void {
  if (!config.workspaces) return;

  const OPUS_45_ID = 'claude-opus-4-5-20251101';
  const OPUS_46_ID = 'claude-opus-4-6';

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;

    if (wsConfig.defaults.model === OPUS_45_ID) {
      wsConfig.defaults.model = OPUS_46_ID;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Add the Opus 4.6 (200K) variant to Anthropic connections that have the 1M variant.
 * This migration ensures both context window options appear in the model picker:
 * 1. Ensures the 1M variant (claude-opus-4-6) is present
 * 2. Adds the 200K variant (claude-opus-4-6-200k) as an alternative
 * 3. Removes any lingering Opus 4.5 entries
 * 4. Deduplicates the models array
 */
function migrateOpus46ContextVariants(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const OPUS_46_ID = 'claude-opus-4-6';
  const OPUS_46_200K_ID = 'claude-opus-4-6-200k';
  const OPUS_45_ID = 'claude-opus-4-5-20251101';

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.providerType !== 'anthropic') continue;
    if (!connection.models || !Array.isArray(connection.models)) continue;

    const modelIds = () => connection.models!.map(m => typeof m === 'string' ? m : m.id);

    // Remove any lingering Opus 4.5 entries
    if (modelIds().includes(OPUS_45_ID)) {
      connection.models = connection.models.filter(m => {
        const id = typeof m === 'string' ? m : m.id;
        return id !== OPUS_45_ID;
      });
      changed = true;
    }

    // If Opus 4.6 (1M) is present but 200K variant is not, add it after the 1M entry
    const currentIds = modelIds();
    if (currentIds.includes(OPUS_46_ID) && !currentIds.includes(OPUS_46_200K_ID)) {
      const opus46Index = currentIds.indexOf(OPUS_46_ID);
      const opus200kDef = getModelById(OPUS_46_200K_ID);
      if (opus200kDef) {
        connection.models.splice(opus46Index + 1, 0, opus200kDef);
        changed = true;
      }
    }

    // Deduplicate
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * Migrate Opus 4.6 to Opus 4.7 for direct Anthropic connections (API key or OAuth).
 * Only applies to anthropic provider type (not compat), as third-party providers
 * like OpenRouter may not support the new model ID yet.
 */
function migrateOpus46ToOpus47(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const idMap = new Map([
    ['claude-opus-4-6', 'claude-opus-4-7'],
    ['claude-opus-4-6-200k', 'claude-opus-4-7-200k'],
  ]);

  let changed = false;

  for (const connection of config.llmConnections) {
    // Only migrate direct Anthropic connections (not compat/third-party)
    if (connection.providerType !== 'anthropic') continue;

    // Migrate defaultModel
    const newDefault = idMap.get(connection.defaultModel as string);
    if (newDefault) {
      connection.defaultModel = newDefault;
      changed = true;
    }

    // Migrate models array
    if (connection.models && Array.isArray(connection.models)) {
      for (const [oldId, newId] of idMap) {
        const hasNew = connection.models.some(m =>
          (typeof m === 'string' ? m : m.id) === newId
        );

        if (hasNew) {
          // New model already exists — just remove the old entry to avoid duplicates
          const before = connection.models.length;
          connection.models = connection.models.filter(m =>
            (typeof m === 'string' ? m : m.id) !== oldId
          );
          if (connection.models.length !== before) changed = true;
        } else {
          // New model doesn't exist — rename the old entry in place
          for (let i = 0; i < connection.models.length; i++) {
            const model = connection.models[i];
            if (typeof model === 'string' && model === oldId) {
              connection.models[i] = newId;
              changed = true;
            } else if (typeof model === 'object' && model.id === oldId) {
              model.id = newId;
              if (model.name?.includes('4.6')) {
                model.name = model.name.replace('4.6', '4.7');
              }
              changed = true;
            }
          }
        }
      }
    }
  }

  return changed;
}

/**
 * Migrate Opus 4.6 to Opus 4.7 in workspace default models.
 * Iterates over all workspaces and updates defaults.model if it's Opus 4.6.
 */
function migrateWorkspaceOpus46ToOpus47(config: StoredConfig): void {
  if (!config.workspaces) return;

  const idMap = new Map([
    ['claude-opus-4-6', 'claude-opus-4-7'],
    ['claude-opus-4-6-200k', 'claude-opus-4-7-200k'],
  ]);

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;

    const newId = idMap.get(wsConfig.defaults.model);
    if (newId) {
      wsConfig.defaults.model = newId;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Add the Opus 4.7 (200K) variant to Anthropic connections that have the 1M variant.
 * This migration ensures both context window options appear in the model picker:
 * 1. Ensures the 1M variant (claude-opus-4-7) is present
 * 2. Adds the 200K variant (claude-opus-4-7-200k) as an alternative
 * 3. Removes any lingering Opus 4.6 entries
 * 4. Deduplicates the models array
 */
function migrateOpus47ContextVariants(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const OPUS_47_ID = 'claude-opus-4-7';
  const OPUS_47_200K_ID = 'claude-opus-4-7-200k';
  const OPUS_46_ID = 'claude-opus-4-6';
  const OPUS_46_200K_ID = 'claude-opus-4-6-200k';

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.providerType !== 'anthropic') continue;
    if (!connection.models || !Array.isArray(connection.models)) continue;

    const modelIds = () => connection.models!.map(m => typeof m === 'string' ? m : m.id);

    // Remove any lingering Opus 4.6 entries
    if (modelIds().includes(OPUS_46_ID) || modelIds().includes(OPUS_46_200K_ID)) {
      connection.models = connection.models.filter(m => {
        const id = typeof m === 'string' ? m : m.id;
        return id !== OPUS_46_ID && id !== OPUS_46_200K_ID;
      });
      changed = true;
    }

    // If Opus 4.7 (1M) is present but 200K variant is not, add it after the 1M entry
    const currentIds = modelIds();
    if (currentIds.includes(OPUS_47_ID) && !currentIds.includes(OPUS_47_200K_ID)) {
      const opus47Index = currentIds.indexOf(OPUS_47_ID);
      const opus200kDef = getModelById(OPUS_47_200K_ID);
      if (opus200kDef) {
        connection.models.splice(opus47Index + 1, 0, opus200kDef);
        changed = true;
      }
    }

    // Deduplicate
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * Migrate Opus 4.7 to Opus 4.8 for direct Anthropic connections (API key or OAuth).
 * Only applies to anthropic provider type (not compat), as third-party providers
 * like OpenRouter may not support the new model ID yet.
 */
function migrateOpus47ToOpus48(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const idMap = new Map([
    ['claude-opus-4-7', 'claude-opus-4-8'],
    ['claude-opus-4-7-200k', 'claude-opus-4-8-200k'],
  ]);

  let changed = false;

  for (const connection of config.llmConnections) {
    // Only migrate direct Anthropic connections (not compat/third-party)
    if (connection.providerType !== 'anthropic') continue;

    // Migrate defaultModel
    const newDefault = idMap.get(connection.defaultModel as string);
    if (newDefault) {
      connection.defaultModel = newDefault;
      changed = true;
    }

    // Migrate models array
    if (connection.models && Array.isArray(connection.models)) {
      for (const [oldId, newId] of idMap) {
        const hasNew = connection.models.some(m =>
          (typeof m === 'string' ? m : m.id) === newId
        );

        if (hasNew) {
          // New model already exists — just remove the old entry to avoid duplicates
          const before = connection.models.length;
          connection.models = connection.models.filter(m =>
            (typeof m === 'string' ? m : m.id) !== oldId
          );
          if (connection.models.length !== before) changed = true;
        } else {
          // New model doesn't exist — rename the old entry in place
          for (let i = 0; i < connection.models.length; i++) {
            const model = connection.models[i];
            if (typeof model === 'string' && model === oldId) {
              connection.models[i] = newId;
              changed = true;
            } else if (typeof model === 'object' && model.id === oldId) {
              model.id = newId;
              if (model.name?.includes('4.7')) {
                model.name = model.name.replace('4.7', '4.8');
              }
              // 4.8 does not need the 1M context beta header — the 1M window
              // is now the default. Clear stale betas carried over from 4.7.
              if (model.id === 'claude-opus-4-8' && model.betas) {
                delete model.betas;
              }
              changed = true;
            }
          }
        }
      }
    }
  }

  return changed;
}

/**
 * Migrate Opus 4.7 to Opus 4.8 in workspace default models.
 * Iterates over all workspaces and updates defaults.model if it's Opus 4.7.
 */
function migrateWorkspaceOpus47ToOpus48(config: StoredConfig): void {
  if (!config.workspaces) return;

  const idMap = new Map([
    ['claude-opus-4-7', 'claude-opus-4-8'],
    ['claude-opus-4-7-200k', 'claude-opus-4-8-200k'],
  ]);

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;

    const newId = idMap.get(wsConfig.defaults.model);
    if (newId) {
      wsConfig.defaults.model = newId;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Add the Opus 4.8 (200K) variant to Anthropic connections that have the 1M variant.
 * This migration ensures both context window options appear in the model picker:
 * 1. Ensures the 1M variant (claude-opus-4-8) is present
 * 2. Adds the 200K variant (claude-opus-4-8-200k) as an alternative
 * 3. Removes any lingering Opus 4.7 entries
 * 4. Deduplicates the models array
 */
function migrateOpus48ContextVariants(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const OPUS_48_ID = 'claude-opus-4-8';
  const OPUS_48_200K_ID = 'claude-opus-4-8-200k';
  const OPUS_47_ID = 'claude-opus-4-7';
  const OPUS_47_200K_ID = 'claude-opus-4-7-200k';

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.providerType !== 'anthropic') continue;
    if (!connection.models || !Array.isArray(connection.models)) continue;

    const modelIds = () => connection.models!.map(m => typeof m === 'string' ? m : m.id);

    // Remove any lingering Opus 4.7 entries
    if (modelIds().includes(OPUS_47_ID) || modelIds().includes(OPUS_47_200K_ID)) {
      connection.models = connection.models.filter(m => {
        const id = typeof m === 'string' ? m : m.id;
        return id !== OPUS_47_ID && id !== OPUS_47_200K_ID;
      });
      changed = true;
    }

    // If Opus 4.8 (1M) is present but 200K variant is not, add it after the 1M entry
    const currentIds = modelIds();
    if (currentIds.includes(OPUS_48_ID) && !currentIds.includes(OPUS_48_200K_ID)) {
      const opus48Index = currentIds.indexOf(OPUS_48_ID);
      const opus200kDef = getModelById(OPUS_48_200K_ID);
      if (opus200kDef) {
        connection.models.splice(opus48Index + 1, 0, opus200kDef);
        changed = true;
      }
    }

    // Deduplicate
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * Migrate the default model from Opus 4.8 → Claude Fable 5 for direct Anthropic
 * connections (API key or OAuth). Only applies to anthropic provider type (not
 * compat), as third-party providers like OpenRouter may not support the new
 * model ID yet.
 *
 * Unlike the in-place Opus N→N+1 renames, this is a model-FAMILY switch, so it
 * uses gentler "replace default" semantics:
 *  - Only connections currently defaulting to Opus 4.8 are touched.
 *  - The default is repointed to the Fable 5 equivalent.
 *  - Fable 5 is inserted into models[] so it is selectable; Opus 4.8 is KEPT
 *    in the list so users can still pick it.
 */
function migrateOpus48ToFable5(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const idMap = new Map([
    ['claude-opus-4-8', 'claude-fable-5'],
    ['claude-opus-4-8-200k', 'claude-fable-5-200k'],
  ]);

  let changed = false;

  for (const connection of config.llmConnections) {
    // Only migrate direct Anthropic connections (not compat/third-party)
    if (connection.providerType !== 'anthropic') continue;

    // Only touch connections that are actually defaulting to Opus 4.8.
    const newDefault = idMap.get(connection.defaultModel as string);
    if (!newDefault) continue;

    connection.defaultModel = newDefault;
    changed = true;

    if (!connection.models || !Array.isArray(connection.models)) continue;

    // Ensure the new default model is selectable. Insert it at the front while
    // keeping the existing Opus 4.8 entry available.
    const ids = connection.models.map(m => (typeof m === 'string' ? m : m.id));
    if (!ids.includes(newDefault)) {
      const def = getModelById(newDefault);
      if (def) {
        connection.models.unshift(def);
      }
    }

    // Deduplicate (defensive, mirrors the other model migrations)
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * Migrate Opus 4.8 → Claude Fable 5 in workspace default models.
 * Iterates over all workspaces and updates defaults.model if it's Opus 4.8.
 */
function migrateWorkspaceOpus48ToFable5(config: StoredConfig): void {
  if (!config.workspaces) return;

  const idMap = new Map([
    ['claude-opus-4-8', 'claude-fable-5'],
    ['claude-opus-4-8-200k', 'claude-fable-5-200k'],
  ]);

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;

    const newId = idMap.get(wsConfig.defaults.model);
    if (newId) {
      wsConfig.defaults.model = newId;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Add the Fable 5.1 (200K) variant to Anthropic connections that list the 1M
 * variant, so both context-window options appear in the model picker.
 * Mirrors migrateOpus48ContextVariants but does NOT remove Opus 4.8 — that
 * model is intentionally kept selectable during the family transition.
 *
 * Repointed from Fable 5.0 to 5.1 in v0.7.427: the 5.0 registry entries were
 * removed, so the old IDs would make getModelById() return undefined and turn
 * this migration into a silent no-op.
 */
function migrateFable51ContextVariants(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const FABLE_5_ID = 'claude-fable-5-1';
  const FABLE_5_200K_ID = 'claude-fable-5-1-200k';

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.providerType !== 'anthropic') continue;
    if (!connection.models || !Array.isArray(connection.models)) continue;

    const modelIds = () => connection.models!.map(m => (typeof m === 'string' ? m : m.id));

    // If Fable 5 (1M) is present but the 200K variant is not, add it after the 1M entry
    const currentIds = modelIds();
    if (currentIds.includes(FABLE_5_ID) && !currentIds.includes(FABLE_5_200K_ID)) {
      const fableIndex = currentIds.indexOf(FABLE_5_ID);
      const fable200kDef = getModelById(FABLE_5_200K_ID);
      if (fable200kDef) {
        connection.models.splice(fableIndex + 1, 0, fable200kDef);
        changed = true;
      }
    }

    // Deduplicate
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * Slug of the Origin-managed "Fable" bundled connection. This connection is
 * re-provisioned from MANAGED_MODEL_PRESETS on every launch by
 * ensureBundledLlmConnections(), so version migrations must leave it alone —
 * dragging it onto a different model family would be undone (and would fight
 * the preset) on the very next start.
 */
const MANAGED_FABLE_CONNECTION_SLUG = 'origin-anthropic-fable';

/**
 * Migrate the default model from Claude Fable 5 → Opus 5 for direct Anthropic
 * connections. Fable remains selectable for users who deliberately choose it.
 *
 * The bundled Fable connection is exempt: users on that connection picked the
 * Fable preset on purpose, and a Fable point release keeps them on Fable
 * (see migrateFable5ToFable51 below).
 */
function migrateFable5ToOpus5(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const idMap = new Map([
    ['claude-fable-5', 'claude-opus-5'],
    ['claude-fable-5-200k', 'claude-opus-5'],
  ]);

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.providerType !== 'anthropic') continue;
    if (connection.slug === MANAGED_FABLE_CONNECTION_SLUG) continue;

    const newDefault = idMap.get(connection.defaultModel as string);
    if (!newDefault) continue;

    connection.defaultModel = newDefault;
    changed = true;

    if (!connection.models || !Array.isArray(connection.models)) continue;

    // Ensure Opus 5 is selectable while keeping the existing Fable entry.
    const ids = connection.models.map(m => (typeof m === 'string' ? m : m.id));
    if (!ids.includes(newDefault)) {
      const def = getModelById(newDefault);
      if (def) {
        connection.models.unshift(def);
      }
    }

    // Deduplicate (defensive, mirrors the other model migrations)
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * The connection a workspace actually uses: its own override, or the global default it inherits.
 *
 * Both Fable migrations below must agree on this, otherwise a workspace that pins only
 * `defaults.model` while inheriting the bundled Fable connection falls through the first
 * migration (to Opus 5) and is then skipped by the second (to Fable 5.1).
 */
function resolveWorkspaceConnection(
  config: StoredConfig,
  wsConfig: { defaults?: { defaultLlmConnection?: string } },
): string | undefined {
  return wsConfig.defaults?.defaultLlmConnection ?? config.defaultLlmConnection;
}

/**
 * Migrate Claude Fable 5 → Opus 5 in workspace default models.
 *
 * Workspaces that deliberately inherit the bundled Fable connection are exempt
 * — they are handled by migrateWorkspaceFable5ToFable51 below.
 */
function migrateWorkspaceFable5ToOpus5(config: StoredConfig): void {
  if (!config.workspaces) return;

  const idMap = new Map([
    ['claude-fable-5', 'claude-opus-5'],
    ['claude-fable-5-200k', 'claude-opus-5'],
  ]);

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;
    if (resolveWorkspaceConnection(config, wsConfig) === MANAGED_FABLE_CONNECTION_SLUG) continue;

    const newId = idMap.get(wsConfig.defaults.model);
    if (newId) {
      wsConfig.defaults.model = newId;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Fable 5 → Fable 5.1 (v0.7.427). Anthropic shipped Fable 5.1 and the curated
 * `fable-5` managed preset now serves `claude-fable-5-1`.
 *
 * Scope is deliberately narrow: only the bundled Fable connection. Standalone
 * Anthropic connections that happen to default to Fable 5 keep their existing
 * behavior (migrateFable5ToOpus5 above), which is why this runs AFTER that
 * phase rather than before it.
 *
 * ensureBundledLlmConnections() also rewrites this connection from the preset
 * on every launch; this migration covers the window before provisioning runs
 * (and installs where the bundled Anthropic key is absent, so provisioning
 * skips the preset entirely).
 */
function migrateFable5ToFable51(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  const idMap = new Map([
    ['claude-fable-5', 'claude-fable-5-1'],
    ['claude-fable-5-200k', 'claude-fable-5-1-200k'],
  ]);

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.slug !== MANAGED_FABLE_CONNECTION_SLUG) continue;

    const newDefault = idMap.get(connection.defaultModel as string);
    if (newDefault) {
      connection.defaultModel = newDefault;
      changed = true;
    }

    if (!connection.models || !Array.isArray(connection.models)) continue;

    // Swap any Fable 5.0 entries for their 5.1 equivalents in place, so the
    // picker never offers a model that no longer exists in MODEL_REGISTRY.
    connection.models = connection.models.map(m => {
      const id = typeof m === 'string' ? m : m.id;
      const replacement = idMap.get(id);
      if (!replacement) return m;
      const def = getModelById(replacement);
      if (!def) return m;
      changed = true;
      return typeof m === 'string' ? (replacement as unknown as typeof m) : def;
    });

    // Deduplicate (defensive, mirrors the other model migrations)
    const seen = new Set<string>();
    const beforeLength = connection.models.length;
    connection.models = connection.models.filter(m => {
      const id = typeof m === 'string' ? m : m.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (connection.models.length !== beforeLength) {
      changed = true;
    }
  }

  return changed;
}

/**
 * Migrate Claude Fable 5 → Fable 5.1 in workspace default models, for
 * workspaces pinned to the bundled Fable connection.
 */
function migrateWorkspaceFable5ToFable51(config: StoredConfig): void {
  if (!config.workspaces) return;

  const idMap = new Map([
    ['claude-fable-5', 'claude-fable-5-1'],
    ['claude-fable-5-200k', 'claude-fable-5-1-200k'],
  ]);

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;
    if (resolveWorkspaceConnection(config, wsConfig) !== MANAGED_FABLE_CONNECTION_SLUG) continue;

    const newId = idMap.get(wsConfig.defaults.model);
    if (newId) {
      wsConfig.defaults.model = newId;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Migrate the default model from Claude Opus 5 → Opus 5.5 for direct Anthropic
 * connections. Opus 5 remains selectable. The bundled Fable connection is exempt.
 */
function migrateOpus5ToOpus55(config: StoredConfig): boolean {
  if (!config.llmConnections) return false;

  let changed = false;

  for (const connection of config.llmConnections) {
    if (connection.providerType !== 'anthropic') continue;
    if (connection.slug === MANAGED_FABLE_CONNECTION_SLUG) continue;
    if (connection.defaultModel !== 'claude-opus-5') continue;

    connection.defaultModel = 'claude-opus-5-5';
    changed = true;

    if (!connection.models || !Array.isArray(connection.models)) continue;

    const ids = connection.models.map(m => (typeof m === 'string' ? m : m.id));
    if (!ids.includes('claude-opus-5-5')) {
      const def = getModelById('claude-opus-5-5');
      if (def) {
        connection.models.unshift(def);
      }
    }
  }

  return changed;
}

/**
 * Move workspace default models off the retired Opus 5 and GPT-5.6 Sol/Luna ids.
 * GPT ids only move on Origin-managed connections, whose model lists come from
 * MANAGED_MODEL_PRESETS; user-defined Pi connections keep their own model lists.
 */
function migrateWorkspaceManagedModelUpgrades(config: StoredConfig): void {
  if (!config.workspaces) return;

  const anthropicIdMap = new Map([['claude-opus-5', 'claude-opus-5-5']]);
  const managedPiIdMap = new Map([
    ['pi/gpt-5.6-sol', 'pi/gpt-6-sol'],
    ['pi/gpt-5.6-luna', 'pi/gpt-6-luna'],
  ]);

  for (const workspace of config.workspaces) {
    const wsConfig = loadWorkspaceConfig(workspace.rootPath);
    if (!wsConfig?.defaults?.model) continue;

    const connection = resolveWorkspaceConnection(config, wsConfig);
    if (connection === MANAGED_FABLE_CONNECTION_SLUG) continue;

    const newId = anthropicIdMap.get(wsConfig.defaults.model)
      ?? (connection && isManagedLlmConnectionSlug(connection)
        ? managedPiIdMap.get(wsConfig.defaults.model)
        : undefined);
    if (newId) {
      wsConfig.defaults.model = newId;
      saveWorkspaceConfig(workspace.rootPath, wsConfig);
    }
  }
}

/**
 * Migrate modelDefaults onto connection.defaultModel, then delete modelDefaults.
 * If user had set modelDefaults.anthropic, apply it to the default anthropic connection.
 * Same for openai. Then remove modelDefaults from config.
 */
function migrateModelDefaultsToConnections(config: StoredConfig): boolean {
  const configAny = config as any;
  if (!configAny.modelDefaults || !config.llmConnections) return false;
  let changed = false;

  // Apply anthropic model default to the default anthropic connection
  if (configAny.modelDefaults.anthropic) {
    const defaultSlug = config.defaultLlmConnection;
    const anthropicConn = config.llmConnections.find(c =>
      c.slug === defaultSlug && (c.providerType === 'anthropic' || c.providerType === 'anthropic_compat')
    ) || config.llmConnections.find(c =>
      c.providerType === 'anthropic' || c.providerType === 'anthropic_compat'
    );
    if (anthropicConn) {
      anthropicConn.defaultModel = configAny.modelDefaults.anthropic;
      changed = true;
    }
  }

  // Apply openai model default to the default openai connection
  // Cast providerType to string for legacy values removed from LlmProviderType
  if (configAny.modelDefaults.openai) {
    const openaiConn = config.llmConnections.find(c =>
      (c.providerType as string) === 'openai' || (c.providerType as string) === 'openai_compat'
    );
    if (openaiConn) {
      openaiConn.defaultModel = configAny.modelDefaults.openai;
      changed = true;
    }
  }

  // Delete modelDefaults
  delete configAny.modelDefaults;
  changed = true;

  return changed;
}

/**
 * Migrate legacy auth config to LLM connections.
 * Call this on app startup before any getLlmConnections() calls.
 *
 * This is a one-time migration that converts:
 * - Legacy authType field → LlmConnection in llmConnections array
 * - Legacy anthropicBaseUrl → LlmConnection.baseUrl
 * - Legacy customModel → LlmConnection.defaultModel
 * - Legacy model → modelDefaults (per provider)
 *
 * After migration, the legacy fields are deleted since they are no longer used.
 */
export function migrateLegacyLlmConnectionsConfig(): void {
  const config = loadStoredConfig();
  if (!config) return;

  const normalizeModelList = (models?: Array<{ id: string } | string>): string[] => {
    if (!models) return [];
    return models
      .map(model => (typeof model === 'string' ? model : model.id))
      .filter(Boolean);
  };

  const applyCompatDefaults = (target: StoredConfig): boolean => {
    if (!target.llmConnections) return false;
    let changed = false;
    for (const connection of target.llmConnections) {
      // Cast to string for legacy 'openai_compat' values that may still exist on disk
      const providerStr = connection.providerType as string;
      if (providerStr !== 'anthropic_compat' && providerStr !== 'openai_compat') {
        continue;
      }
      const compatDefaults = getDefaultModelsForConnection(connection.providerType).map(
        m => typeof m === 'string' ? m : m.id
      );
      const normalizedModels = normalizeModelList(connection.models);
      if (normalizedModels.length === 0) {
        connection.models = [...compatDefaults];
        changed = true;
      } else if (normalizedModels.length !== (connection.models?.length ?? 0)) {
        connection.models = [...normalizedModels];
        changed = true;
      }
      // Backfill any new default models that are missing from existing connections
      // (e.g., Sonnet added to compat defaults after user already created connection)
      let currentModels = normalizeModelList(connection.models);
      for (const defaultModel of compatDefaults) {
        if (!currentModels.includes(defaultModel)) {
          currentModels = [...currentModels, defaultModel];
          changed = true;
        }
      }
      if (changed) {
        connection.models = currentModels;
      }
      const currentDefault = connection.defaultModel?.trim();
      if (!currentDefault) {
        connection.defaultModel = (normalizeModelList(connection.models)[0] ?? compatDefaults[0]);
        changed = true;
      } else if (!normalizeModelList(connection.models).includes(currentDefault)) {
        connection.models = [currentDefault, ...normalizeModelList(connection.models).filter(m => m !== currentDefault)];
        changed = true;
      }
    }
    return changed;
  };

  // Already migrated - llmConnections array exists
  if (config.llmConnections !== undefined) {
    // Clean up any remaining legacy fields from previous runs
    let needsSave = false;
    const configAny = config as any;
    if ('authType' in config) {
      delete configAny.authType;
      needsSave = true;
    }
    if ('anthropicBaseUrl' in config) {
      delete configAny.anthropicBaseUrl;
      needsSave = true;
    }
    if ('customModel' in config) {
      delete configAny.customModel;
      needsSave = true;
    }
    if ('model' in config) {
      const legacyModel = configAny.model as string | undefined;
      if (legacyModel) {
        const provider = getModelProvider(legacyModel) ?? 'anthropic';
        configAny.modelDefaults = { ...(configAny.modelDefaults ?? {}), [provider]: legacyModel };
      }
      delete configAny.model;
      needsSave = true;
    }
    // Note: applyCompatDefaults() is NOT called here for already-migrated configs.
    // Compat connections are user-owned after creation — the app should not
    // silently extend or override the user's model list on every startup.
    // Compat defaults are only applied during fresh connection creation or
    // first-time legacy migration (the config.llmConnections === undefined path below).

    // Phase 1a-bis: Migrate Codex/Copilot connections to Pi backend
    if (migrateCodexCopilotToPi(config)) {
      needsSave = true;
    }

    // Phase 1b: Backfill models/defaultModel on ALL connections (not just compat)
    // This ensures built-in connections (anthropic, openai) always have models populated
    if (backfillAllConnectionModels(config)) {
      needsSave = true;
    }
    // Phase 1c: Migrate modelDefaults onto connection.defaultModel, then delete modelDefaults
    if (migrateModelDefaultsToConnections(config)) {
      needsSave = true;
    }
    // Phase 1d: Migrate Opus 4.5 → Opus 4.6 for direct Anthropic connections
    if (migrateOpus45ToOpus46(config)) {
      needsSave = true;
    }
    // Phase 1e: Migrate Opus 4.5 → Opus 4.6 in workspace default models
    migrateWorkspaceOpus45ToOpus46(config);
    // Phase 1f: Add Opus 4.6 (200K) variant alongside the 1M default
    if (migrateOpus46ContextVariants(config)) {
      needsSave = true;
    }
    // Phase 1g: Migrate Sonnet 4.5 → Sonnet 4.6 for direct Anthropic connections
    if (migrateSonnet45ToSonnet46(config)) {
      needsSave = true;
    }
    // Phase 1h: Migrate Sonnet 4.5 → Sonnet 4.6 in workspace default models
    migrateWorkspaceSonnet45ToSonnet46(config);
    // Phase 1i: Migrate Opus 4.6 → Opus 4.7 for direct Anthropic connections
    if (migrateOpus46ToOpus47(config)) {
      needsSave = true;
    }
    // Phase 1j: Migrate Opus 4.6 → Opus 4.7 in workspace default models
    migrateWorkspaceOpus46ToOpus47(config);
    // Phase 1k: Add Opus 4.7 (200K) variant alongside the 1M default
    if (migrateOpus47ContextVariants(config)) {
      needsSave = true;
    }
    // Phase 1l: Migrate Opus 4.7 → Opus 4.8 for direct Anthropic connections
    if (migrateOpus47ToOpus48(config)) {
      needsSave = true;
    }
    // Phase 1m: Migrate Opus 4.7 → Opus 4.8 in workspace default models
    migrateWorkspaceOpus47ToOpus48(config);
    // Phase 1n: Add Opus 4.8 (200K) variant alongside the 1M default
    //          and remove any lingering 4.7 entries. Runs BEFORE the Opus→Fable
    //          repoint so a 4.7 user is first normalized onto the 4.8 family.
    if (migrateOpus48ContextVariants(config)) {
      needsSave = true;
    }
    // Phase 1o: Migrate Opus 4.8 → Claude Fable 5 for direct Anthropic connections.
    //          Fable 5 access (suspended 2026-06-12 by a US-gov directive,
    //          https://www.anthropic.com/news/fable-mythos-access) has been
    //          restored, so Fable 5 is once again the default flagship. Opus 4.8
    //          entries are KEPT in models[] so they stay selectable.
    //          This historical step remains enabled so older installs are
    //          normalized before the current Fable 5 → Opus 5 migration below.
    if (migrateOpus48ToFable5(config)) {
      needsSave = true;
    }
    // Phase 1p: Migrate Opus 4.8 → Fable 5 in workspace default models
    migrateWorkspaceOpus48ToFable5(config);
    // Phase 1q: Migrate the outgoing Fable 5 default to Opus 5. The earlier
    // phases remain enabled so installs on any prior flagship reach Opus 5.
    // The bundled Fable connection is exempt — see phase 1s.
    if (migrateFable5ToOpus5(config)) {
      needsSave = true;
    }
    // Phase 1r: Migrate Fable 5 → Opus 5 in workspace default models
    migrateWorkspaceFable5ToOpus5(config);
    // Phase 1s: Migrate Fable 5 → Fable 5.1 on the bundled Fable connection.
    //           Runs AFTER 1q so that standalone Anthropic connections keep
    //           their existing Fable 5 → Opus 5 behavior; only users who chose
    //           the curated Fable preset stay in the Fable family.
    if (migrateFable5ToFable51(config)) {
      needsSave = true;
    }
    // Phase 1t: Migrate Fable 5 → Fable 5.1 in workspace default models
    migrateWorkspaceFable5ToFable51(config);
    // Phase 1u: Add the Fable 5.1 (200K) variant alongside the 1M default.
    //           Runs LAST so a connection repointed by 1s reaches its final
    //           state in a single launch rather than converging over two.
    if (migrateFable51ContextVariants(config)) {
      needsSave = true;
    }
    // Phase 1v: Migrate the outgoing Opus 5 default to Opus 5.5. Runs after 1q
    //           so installs still on Fable 5 reach Opus 5.5 in a single launch.
    if (migrateOpus5ToOpus55(config)) {
      needsSave = true;
    }
    // Phase 1w: Move workspace defaults off Opus 5 and the GPT-5.6 Sol/Luna ids
    migrateWorkspaceManagedModelUpgrades(config);

    if (needsSave) {
      saveConfig(config);
    }
    return;
  }

  // Initialize empty array
  config.llmConnections = [];

  // Legacy migration: if user had authType set, create a connection for them
  const configAny = config as any;
  const legacyAuthType = configAny.authType as AuthType | undefined;
  const legacyBaseUrl = configAny.anthropicBaseUrl as string | undefined;
  const legacyCustomModel = configAny.customModel as string | undefined;
  const legacyModel = configAny.model as string | undefined;

  if (legacyAuthType) {
    let migrated: LlmConnection | null = null;

    if (legacyAuthType === 'oauth_token') {
      // Claude Max OAuth
      migrated = {
        slug: 'claude-max',
        name: 'Claude Max',
        providerType: 'anthropic',
        authType: 'oauth',
        models: getDefaultModelsForConnection('anthropic'),
        createdAt: Date.now(),
      };
    } else if (legacyAuthType === 'codex_oauth') {
      // ChatGPT Plus OAuth → Pi backend
      migrated = {
        slug: 'codex',
        name: 'ChatGPT Plus (via Pi)',
        providerType: 'pi',
        authType: 'oauth',
        piAuthProvider: 'openai-codex',
        modelSelectionMode: 'automaticallySyncedFromProvider',
        models: getDefaultModelsForConnection('pi', 'openai-codex'),
        createdAt: Date.now(),
      };
    } else if (legacyAuthType === 'codex_api_key') {
      // OpenAI API Key → Pi backend
      migrated = {
        slug: 'codex-api',
        name: 'OpenAI API (via Pi)',
        providerType: 'pi',
        authType: 'api_key',
        piAuthProvider: 'openai',
        modelSelectionMode: 'automaticallySyncedFromProvider',
        models: getDefaultModelsForConnection('pi', 'openai'),
        createdAt: Date.now(),
      };
    } else if (legacyAuthType === 'api_key') {
      // Anthropic API Key - check if custom endpoint (compat mode)
      const hasCustomEndpoint = !!legacyBaseUrl;
      const providerType = hasCustomEndpoint ? 'anthropic_compat' as const : 'anthropic' as const;
      migrated = {
        slug: 'anthropic-api',
        name: hasCustomEndpoint ? 'Custom Anthropic-Compatible' : 'Anthropic (API Key)',
        providerType,
        authType: hasCustomEndpoint ? 'api_key_with_endpoint' : 'api_key',
        models: getDefaultModelsForConnection(providerType),
        createdAt: Date.now(),
      };
    }

    if (migrated) {
      // Validate the migrated connection has a valid provider/auth combination
      if (!isValidProviderAuthCombination(migrated.providerType, migrated.authType)) {
        console.warn(
          `[config] Legacy migration created invalid provider/auth combination: ` +
          `providerType=${migrated.providerType}, authType=${migrated.authType} ` +
          `(slug: ${migrated.slug}). Skipping migration for this connection.`
        );
      } else {
        // Apply legacy baseUrl if set
        if (legacyBaseUrl) {
          migrated.baseUrl = legacyBaseUrl;
        }

        // Apply legacy customModel if set
        if (legacyCustomModel) {
          migrated.defaultModel = legacyCustomModel;
        }

        config.llmConnections.push(migrated);
        config.defaultLlmConnection = migrated.slug;
      }
    }
  }

  // Delete legacy fields after migration
  delete configAny.authType;
  delete configAny.anthropicBaseUrl;
  delete configAny.customModel;
  delete configAny.model;

  if (legacyModel) {
    const provider = getModelProvider(legacyModel) ?? 'anthropic';
    configAny.modelDefaults = { ...(configAny.modelDefaults ?? {}), [provider]: legacyModel };
  }

  // Run the same backfill and migration on newly created connections
  migrateCodexCopilotToPi(config);
  backfillAllConnectionModels(config);
  migrateModelDefaultsToConnections(config);

  saveConfig(config);
}

/**
 * Fix defaultLlmConnection references that point to non-existent connections.
 * This can happen when a connection is removed or was never created
 * (e.g. "anthropic-api" is set as default but only "claude-max" exists).
 *
 * Fixes both the global defaultLlmConnection and per-workspace defaults.
 * Called on app startup alongside other migrations.
 */
export function migrateOrphanedDefaultConnections(): void {
  const config = loadStoredConfig();
  if (!config) return;
  if (!config.llmConnections || config.llmConnections.length === 0) return;

  let changed = false;

  // Fix global default if it points to a non-existent connection
  if (ensureDefaultLlmConnection(config)) {
    changed = true;
  }

  // Fix workspace defaults that point to non-existent connections
  try {
    const workspaces = getWorkspaces();
    for (const ws of workspaces) {
      const wsConfig = loadWorkspaceConfig(ws.rootPath);
      if (wsConfig?.defaults?.defaultLlmConnection) {
        const exists = config.llmConnections.some(
          c => c.slug === wsConfig.defaults!.defaultLlmConnection
        );
        if (!exists) {
          delete wsConfig.defaults.defaultLlmConnection;
          saveWorkspaceConfig(ws.rootPath, wsConfig);
        }
      }
    }
  } catch (error) {
    console.error('Failed to clean up workspace default connection references:', error);
  }

  if (changed) {
    saveConfig(config);
  }
}

/**
 * Ensure default LLM connection is set correctly.
 * Called internally by write operations to fix inconsistent state.
 * This is NOT called on read - reads never modify config.
 */
function ensureDefaultLlmConnection(config: StoredConfig): boolean {
  if (!config.llmConnections || config.llmConnections.length === 0) {
    return false;
  }

  const defaultExists = config.llmConnections.some(c => c.slug === config.defaultLlmConnection);
  if (!config.defaultLlmConnection || !defaultExists) {
    config.defaultLlmConnection = config.llmConnections[0]!.slug;
    return true;
  }

  return false;
}

/**
 * Migrate legacy global credentials to LLM connection-scoped credentials.
 * This ensures that credentials saved before the LLM connections system
 * are available through the new connection-based auth.
 *
 * Called on app startup (async operation, credentials use encrypted storage).
 *
 * Migration mapping:
 * - claude_oauth::global → llm_oauth::claude-max
 * - anthropic_api_key::global → llm_api_key::anthropic-api
 *
 * After successful migration, legacy credentials are deleted to prevent
 * stale data and reduce credential store clutter.
 */
export async function migrateLegacyCredentials(): Promise<void> {
  const manager = getCredentialManager();
  const debug = (await import('../utils/debug.ts')).debug;

  // Migrate Claude OAuth: claude_oauth::global → llm_oauth::claude-max
  const legacyClaudeOAuth = await manager.getClaudeOAuthCredentials();
  if (legacyClaudeOAuth?.accessToken) {
    // Only migrate if llm_oauth::claude-max doesn't exist yet
    const existingLlmOAuth = await manager.getLlmOAuth('claude-max');
    if (!existingLlmOAuth) {
      await manager.setLlmOAuth('claude-max', {
        accessToken: legacyClaudeOAuth.accessToken,
        refreshToken: legacyClaudeOAuth.refreshToken,
        expiresAt: legacyClaudeOAuth.expiresAt,
      });
      debug('[storage] Migrated legacy Claude OAuth to llm_oauth::claude-max');

      // Delete legacy credential after successful migration
      // Global credentials use just the type - the key format is {type}::global
      try {
        await manager.delete({ type: 'claude_oauth' });
        debug('[storage] Deleted legacy claude_oauth::global credential');
      } catch (error) {
        debug('[storage] Failed to delete legacy claude_oauth::global:', error);
      }
    }
  }

  // Migrate Anthropic API key: anthropic_api_key::global → llm_api_key::anthropic-api
  const legacyApiKey = await manager.getApiKey();
  if (legacyApiKey) {
    // Only migrate if llm_api_key::anthropic-api doesn't exist yet
    const existingLlmApiKey = await manager.getLlmApiKey('anthropic-api');
    if (!existingLlmApiKey) {
      await manager.setLlmApiKey('anthropic-api', legacyApiKey);
      debug('[storage] Migrated legacy Anthropic API key to llm_api_key::anthropic-api');

      // Delete legacy credential after successful migration
      // Global credentials use just the type - the key format is {type}::global
      try {
        await manager.delete({ type: 'anthropic_api_key' });
        debug('[storage] Deleted legacy anthropic_api_key::global credential');
      } catch (error) {
        debug('[storage] Failed to delete legacy anthropic_api_key::global:', error);
      }
    }
  }
}

/**
 * Get all LLM connections.
 * Returns only user-added connections (no auto-populated built-ins).
 *
 * Note: This function is read-only and never modifies config.
 * Call migrateLegacyLlmConnectionsConfig() on app startup to handle migration.
 */
export function getLlmConnections(): LlmConnection[] {
  const config = loadStoredConfig();
  if (!config) return [];

  // Return empty array if not migrated yet - caller should call migration on startup
  return config.llmConnections || [];
}

/**
 * Idempotently provision Origin-managed LLM connections and their bundled
 * credentials. Existing user-managed defaults are preserved; installs still
 * using a former bundled default (Sol or Astra) are migrated to Opus 5.5.
 */
export async function ensureBundledLlmConnections(apiKeys: {
  openai?: string;
  anthropic?: string;
  xai?: string;
}): Promise<string[]> {
  const config = loadStoredConfig();
  if (!config) return [];

  config.llmConnections ??= [];
  const availableSlugs: string[] = [];
  const manager = getCredentialManager();
  const keyByPreset = new Map([
    ['gpt-5.6-sol', apiKeys.openai],
    ['gpt-5.6-luna', apiKeys.openai],
    ['gpt-6-astra', apiKeys.openai],
    ['opus-5', apiKeys.anthropic],
    ['fable-5', apiKeys.anthropic],
  ]);

  for (const preset of MANAGED_MODEL_PRESETS) {
    const apiKey = keyByPreset.get(preset.id);
    if (!apiKey) continue;

    const index = config.llmConnections.findIndex((connection) => connection.slug === preset.connectionSlug);
    const existing = index >= 0 ? config.llmConnections[index] : undefined;
    const connection: LlmConnection = {
      ...preset.connection,
      createdAt: existing?.createdAt ?? Date.now(),
      lastUsedAt: existing?.lastUsedAt,
    };

    if (index >= 0) {
      config.llmConnections[index] = connection;
    } else {
      config.llmConnections.push(connection);
    }

    await manager.setLlmApiKey(preset.connectionSlug, apiKey);
    availableSlugs.push(preset.connectionSlug);
  }

  const defaultManaged = getDefaultManagedModelPreset();
  const preferredManagedSlug = availableSlugs.includes(defaultManaged.connectionSlug)
    ? defaultManaged.connectionSlug
    : availableSlugs.includes('origin-openai')
      ? 'origin-openai'
      : config.llmConnections[0]?.slug;

  const managedSlugs = new Set(MANAGED_MODEL_PRESETS.map((preset) => preset.connectionSlug));
  const retiredBundled = (config.llmConnections ?? []).filter(
    (connection) => connection.isBundled && !managedSlugs.has(connection.slug),
  );
  if (retiredBundled.length > 0) {
    const retiredSlugs = new Set(retiredBundled.map((connection) => connection.slug));
    config.llmConnections = config.llmConnections.filter((connection) => !retiredSlugs.has(connection.slug));
    for (const slug of retiredSlugs) {
      await manager.deleteLlmCredentials(slug).catch(() => {});
    }
    if (config.defaultLlmConnection && retiredSlugs.has(config.defaultLlmConnection)) {
      config.defaultLlmConnection = preferredManagedSlug;
    }
    for (const workspace of config.workspaces) {
      const workspaceConfig = loadWorkspaceConfig(workspace.rootPath);
      if (!workspaceConfig?.defaults) continue;
      const workspaceConnection = workspaceConfig.defaults.defaultLlmConnection;
      if (!workspaceConnection || !retiredSlugs.has(workspaceConnection)) continue;
      workspaceConfig.defaults.defaultLlmConnection = preferredManagedSlug;
      if (workspaceConfig.defaults.model === 'pi/grok-4.5') {
        workspaceConfig.defaults.model = defaultManaged.model;
      }
      saveWorkspaceConfig(workspace.rootPath, workspaceConfig);
    }
  }

  const hasManagedOpenAi = availableSlugs.includes(defaultManaged.connectionSlug)
    || availableSlugs.includes('origin-openai');
  const shouldMigrateManagedDefaults = hasManagedOpenAi && !config.managedModelDefaultsMigrated;
  const previousDefault = config.defaultLlmConnection;
  const usedOldBundledDefault = !previousDefault
    || previousDefault === 'anthropic-api'
    || previousDefault === 'origin-anthropic';

  if (shouldMigrateManagedDefaults && usedOldBundledDefault) {
    config.defaultLlmConnection = preferredManagedSlug;
  } else {
    ensureDefaultLlmConnection(config);
  }

  // Keep workspace defaults inheriting from the global connection. Only update
  // an explicit model when it is still one of Relay's former managed defaults.
  if (shouldMigrateManagedDefaults) {
    const formerManagedModels = new Set([
      'claude-opus-5',
      'claude-fable-5-1',
      'claude-fable-5-1-200k',
      'claude-fable-5',
      'claude-fable-5-200k',
      'claude-opus-4-8',
      'claude-opus-4-8-200k',
      'pi/gpt-5.6-sol',
    ]);

    for (const workspace of config.workspaces) {
      const workspaceConfig = loadWorkspaceConfig(workspace.rootPath);
      if (!workspaceConfig?.defaults) continue;

      const workspaceConnection = workspaceConfig.defaults.defaultLlmConnection;
      const usedOldWorkspaceDefault = !workspaceConnection
        || workspaceConnection === 'anthropic-api'
        || workspaceConnection === 'origin-anthropic';
      if (!usedOldWorkspaceDefault) continue;

      let changed = false;
      const hasCustomizedInheritedModel = !workspaceConnection
        && !!workspaceConfig.defaults.model
        && !formerManagedModels.has(workspaceConfig.defaults.model);
      if (hasCustomizedInheritedModel && previousDefault) {
        // The workspace deliberately overrode the old provider's model while
        // inheriting its connection. Pin that previous connection so changing
        // the app default does not create an invalid provider/model pairing.
        workspaceConfig.defaults.defaultLlmConnection = previousDefault;
        changed = true;
        saveWorkspaceConfig(workspace.rootPath, workspaceConfig);
        continue;
      }
      if (workspaceConnection) {
        workspaceConfig.defaults.defaultLlmConnection = preferredManagedSlug;
        changed = true;
      }
      if (!workspaceConfig.defaults.model || formerManagedModels.has(workspaceConfig.defaults.model)) {
        workspaceConfig.defaults.model = defaultManaged.model;
        changed = true;
      }
      if (changed) saveWorkspaceConfig(workspace.rootPath, workspaceConfig);
    }

    config.managedModelDefaultsMigrated = true;
  }

  const hasDefaultManaged = availableSlugs.includes(defaultManaged.connectionSlug);
  const shouldMigrateOpus55Default = hasDefaultManaged && !config.managedOpus55DefaultMigrated;
  if (shouldMigrateOpus55Default) {
    const formerDefaultConnections = new Set(['origin-openai-astra']);
    const formerDefaultModels = new Set(['pi/gpt-6-astra']);
    if (!config.managedAstraDefaultMigrated) {
      formerDefaultConnections.add('origin-openai');
      formerDefaultModels.add('pi/gpt-5.6-sol');
    }
    const usedFormerDefault = !config.defaultLlmConnection
      || formerDefaultConnections.has(config.defaultLlmConnection);
    if (usedFormerDefault) {
      config.defaultLlmConnection = defaultManaged.connectionSlug;
    }

    for (const workspace of config.workspaces) {
      const workspaceConfig = loadWorkspaceConfig(workspace.rootPath);
      if (!workspaceConfig?.defaults) continue;

      const workspaceConnection = workspaceConfig.defaults.defaultLlmConnection;
      const inheritsFormerDefault = !workspaceConnection
        || formerDefaultConnections.has(workspaceConnection);
      if (!inheritsFormerDefault) continue;

      const model = workspaceConfig.defaults.model;
      const usesFormerDefaultModel = !model || formerDefaultModels.has(model);
      if (!usesFormerDefaultModel) continue;

      workspaceConfig.defaults.defaultLlmConnection = defaultManaged.connectionSlug;
      workspaceConfig.defaults.model = defaultManaged.model;
      saveWorkspaceConfig(workspace.rootPath, workspaceConfig);
    }

    config.managedAstraDefaultMigrated = true;
    config.managedOpus55DefaultMigrated = true;
  }

  saveConfig(config);

  return availableSlugs;
}

/**
 * Get a specific LLM connection by slug.
 * @param slug - Connection slug
 * @returns Connection or null if not found
 */
export function getLlmConnection(slug: string): LlmConnection | null {
  const connections = getLlmConnections();
  return connections.find(c => c.slug === slug) || null;
}

/**
 * Add a new LLM connection.
 * @param connection - Connection to add (slug must be unique)
 * @returns true if added, false if slug already exists
 */
export function addLlmConnection(connection: LlmConnection): boolean {
  const config = loadStoredConfig();
  if (!config) return false;

  // Initialize array if not yet migrated (safe default for write operations)
  if (!config.llmConnections) {
    config.llmConnections = [];
  }

  // Check for duplicate slug
  if (config.llmConnections.some(c => c.slug === connection.slug)) {
    return false;
  }

  // Add connection with timestamp
  config.llmConnections.push({
    ...connection,
    createdAt: connection.createdAt || Date.now(),
  });

  // Ensure default is set after adding first connection
  ensureDefaultLlmConnection(config);

  saveConfig(config);
  return true;
}

/**
 * Update an existing LLM connection.
 * @param slug - Connection slug to update
 * @param updates - Partial updates to apply (slug is ignored)
 * @returns true if updated, false if not found
 */
export function updateLlmConnection(slug: string, updates: Partial<Omit<LlmConnection, 'slug'>>): boolean {
  const config = loadStoredConfig();
  if (!config) return false;

  // No connections means nothing to update
  if (!config.llmConnections || config.llmConnections.length === 0) {
    return false;
  }

  const connections = config.llmConnections;
  const index = connections.findIndex(c => c.slug === slug);
  if (index === -1) return false;

  const existing = connections[index]!;
  const toModelIds = (models?: Array<{ id: string } | string>): string[] =>
    (models ?? []).map(m => typeof m === 'string' ? m : m.id);

  connections[index] = {
    // Preserve required fields from existing
    slug: existing.slug,
    name: updates.name ?? existing.name,
    providerType: updates.providerType ?? existing.providerType,
    type: updates.type ?? existing.type, // Legacy field
    authType: updates.authType ?? existing.authType,
    createdAt: updates.createdAt ?? existing.createdAt,
    // Optional fields from updates or existing
    baseUrl: updates.baseUrl !== undefined ? updates.baseUrl : existing.baseUrl,
    models: updates.models !== undefined ? updates.models : existing.models,
    defaultModel: updates.defaultModel !== undefined ? updates.defaultModel : existing.defaultModel,
    modelSelectionMode: updates.modelSelectionMode !== undefined ? updates.modelSelectionMode : existing.modelSelectionMode,
    // Cloud provider fields
    awsRegion: updates.awsRegion !== undefined ? updates.awsRegion : existing.awsRegion,
    gcpProjectId: updates.gcpProjectId !== undefined ? updates.gcpProjectId : existing.gcpProjectId,
    gcpRegion: updates.gcpRegion !== undefined ? updates.gcpRegion : existing.gcpRegion,
    // Pi auth provider
    piAuthProvider: updates.piAuthProvider !== undefined ? updates.piAuthProvider : existing.piAuthProvider,
    // Timestamps
    lastUsedAt: updates.lastUsedAt !== undefined ? updates.lastUsedAt : existing.lastUsedAt,
    // Managed connection marker
    isBundled: updates.isBundled !== undefined ? updates.isBundled : existing.isBundled,
  };

  const updated = connections[index]!;
  if (updated.providerType === 'pi') {
    const beforeModelIds = toModelIds(existing.models);
    const afterModelIds = toModelIds(updated.models);
    const changed =
      existing.defaultModel !== updated.defaultModel ||
      existing.modelSelectionMode !== updated.modelSelectionMode ||
      !modelSetEquals(beforeModelIds, afterModelIds);

    if (changed) {
      const stack = (new Error().stack ?? '').split('\n').slice(2, 7).map(s => s.trim());
      debug('[storage] updateLlmConnection(pi) changed', {
        slug,
        before: {
          mode: existing.modelSelectionMode,
          defaultModel: existing.defaultModel,
          modelCount: beforeModelIds.length,
          modelsFirst5: beforeModelIds.slice(0, 5),
        },
        after: {
          mode: updated.modelSelectionMode,
          defaultModel: updated.defaultModel,
          modelCount: afterModelIds.length,
          modelsFirst5: afterModelIds.slice(0, 5),
        },
        updates: {
          keys: Object.keys(updates),
          defaultModel: updates.defaultModel,
          modelSelectionMode: updates.modelSelectionMode,
          modelsCount: Array.isArray(updates.models) ? updates.models.length : undefined,
        },
        stack,
      });
    }
  }

  saveConfig(config);
  return true;
}

/**
 * Delete an LLM connection.
 * @param slug - Connection slug to delete
 * @returns true if deleted, false if not found
 */
export function deleteLlmConnection(slug: string): boolean {
  const config = loadStoredConfig();
  if (!config) return false;

  // No connections means nothing to delete
  if (!config.llmConnections || config.llmConnections.length === 0) {
    return false;
  }

  const connections = config.llmConnections;
  const index = connections.findIndex(c => c.slug === slug);
  if (index === -1) return false;

  connections.splice(index, 1);

  // If deleted connection was the default, reset to first remaining or clear
  if (config.defaultLlmConnection === slug) {
    config.defaultLlmConnection = connections.length > 0 ? connections[0]!.slug : undefined;
  }

  saveConfig(config);

  // Clean up workspace references to the deleted connection (non-blocking)
  try {
    const workspaces = getWorkspaces();
    for (const ws of workspaces) {
      const wsConfig = loadWorkspaceConfig(ws.rootPath);
      if (wsConfig?.defaults?.defaultLlmConnection === slug) {
        wsConfig.defaults.defaultLlmConnection = undefined;
        saveWorkspaceConfig(ws.rootPath, wsConfig);
      }
    }
  } catch (error) {
    console.error('Failed to clean up workspace references:', error);
  }

  // Clean up stored credentials for this connection (API keys, OAuth tokens)
  // This is fire-and-forget but we log errors for debugging
  const credentialManager = getCredentialManager();
  credentialManager.delete({ type: 'llm_api_key', connectionSlug: slug }).catch((error) => {
    console.error(`[storage] Failed to delete API key credential for connection '${slug}':`, error);
  });
  credentialManager.delete({ type: 'llm_oauth', connectionSlug: slug }).catch((error) => {
    console.error(`[storage] Failed to delete OAuth credential for connection '${slug}':`, error);
  });

  return true;
}

/**
 * Get the default LLM connection slug.
 * @returns Default connection slug, or null if no connections exist
 */
export function getDefaultLlmConnection(): string | null {
  const config = loadStoredConfig();
  if (!config) return null;

  // If no connections, return null
  if (!config.llmConnections || config.llmConnections.length === 0) {
    return null;
  }

  return config.defaultLlmConnection || config.llmConnections[0]?.slug || null;
}

/**
 * Set the default LLM connection.
 * @param slug - Connection slug to set as default
 * @returns true if set, false if connection not found
 */
export function setDefaultLlmConnection(slug: string): boolean {
  const config = loadStoredConfig();
  if (!config) return false;

  // No connections means nothing to set as default
  if (!config.llmConnections || config.llmConnections.length === 0) {
    return false;
  }

  // Verify connection exists
  if (!config.llmConnections.some(c => c.slug === slug)) {
    return false;
  }

  config.defaultLlmConnection = slug;
  saveConfig(config);
  return true;
}

/**
 * Get the app-level default thinking level for new sessions.
 * Falls back to bundled config-defaults when unset.
 */
export function getDefaultThinkingLevel(): ThinkingLevel {
  const config = loadStoredConfig();
  if (config?.defaultThinkingLevel && isValidThinkingLevel(config.defaultThinkingLevel)) {
    return config.defaultThinkingLevel;
  }
  const defaults = loadConfigDefaults();
  return defaults.workspaceDefaults.thinkingLevel;
}

/**
 * Set the app-level default thinking level for new sessions.
 * @returns true if persisted, false if config could not be loaded
 */
export function setDefaultThinkingLevel(level: ThinkingLevel): boolean {
  const config = loadStoredConfig();
  if (!config) return false;

  config.defaultThinkingLevel = level;
  saveConfig(config);
  return true;
}

/**
 * Update the lastUsedAt timestamp for a connection.
 * @param slug - Connection slug
 */
export function touchLlmConnection(slug: string): void {
  const config = loadStoredConfig();
  if (!config) return;

  // No connections means nothing to touch
  if (!config.llmConnections) return;

  const connection = config.llmConnections.find(c => c.slug === slug);
  if (connection) {
    connection.lastUsedAt = Date.now();
    saveConfig(config);
  }
}

// ============================================
// Tool Icons (CLI tool icons for turn card display)
// ============================================

import { copyFileSync } from 'fs';

const TOOL_ICONS_DIR_NAME = 'tool-icons';

/**
 * Returns the path to the tool-icons directory: ~/.origin-relay/tool-icons/
 */
export function getToolIconsDir(): string {
  return join(CONFIG_DIR, TOOL_ICONS_DIR_NAME);
}

/**
 * Ensure tool-icons directory exists and has bundled defaults.
 * Resolves bundled path automatically via getBundledAssetsDir('tool-icons').
 * Copies bundled tool-icons.json and icon files on first run.
 * Only copies files that don't already exist (preserves user customizations).
 */
export function ensureToolIcons(): void {
  const toolIconsDir = getToolIconsDir();

  // Create tool-icons directory if it doesn't exist
  if (!existsSync(toolIconsDir)) {
    mkdirSync(toolIconsDir, { recursive: true });
  }

  // Resolve bundled tool-icons directory via shared asset resolver
  const bundledToolIconsDir = getBundledAssetsDir('tool-icons');
  if (!bundledToolIconsDir) {
    return;
  }

  // Copy each bundled file if it doesn't exist in the target dir
  // This includes tool-icons.json and all icon files (png, ico, svg, jpg)
  try {
    const bundledFiles = readdirSync(bundledToolIconsDir);
    for (const file of bundledFiles) {
      const destPath = join(toolIconsDir, file);
      if (!existsSync(destPath)) {
        const srcPath = join(bundledToolIconsDir, file);
        copyFileSync(srcPath, destPath);
      }
    }
  } catch {
    // Ignore errors — tool icons are optional enhancement
  }
}
